// src/services/api.js

import axios from 'axios';
import toast from 'react-hot-toast';
import {
  DEFAULT_API_BASE_URL,
  API_TIMEOUT,
  USER_DATA_KEY,
  HTTP_UNAUTHORIZED
} from '../constants';
import i18n from '../i18n';


const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL;
export const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || API_BASE_URL.replace('/api', '');
export { API_BASE_URL };

// Helper function para obtener headers (auth is handled via httpOnly cookie)
export const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};


// Configurar axios

const api = axios.create({

  baseURL: API_BASE_URL,

  timeout: API_TIMEOUT,

  withCredentials: true,

  headers: {

    'Content-Type': 'application/json'

  }

});


// Helper para obtener o generar TAB_ID (sincronizado con TenantContext)
export const getTabId = () => {
  let tabId = sessionStorage.getItem('ACMW_TAB_ID');
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('ACMW_TAB_ID', tabId);
  }
  return tabId;
};

// Headers de tenant (X-Publisher-ID / X-Biller-ID) para una llamada a `requestUrl`,
// tomados de la ruta de la pantalla (/billers/{billerId}/...). Los usa el interceptor de
// axios y cualquier llamada que no pase por axios (p. ej. el streaming del asistente):
// el caché de sesión del backend vive en la memoria de cada proceso, así que en
// ambientes con varios servers (DEMO2) estos headers son lo que fija la empresa.
// El backend valida que el usuario tenga acceso a esa empresa.
export const getTenantHeaders = (requestUrl) => {
  if (typeof window === 'undefined') return {};
  const billerMatch = window.location.pathname.match(/\/billers\/([^\/]+)/);
  if (!billerMatch) return {};
  const billerId = decodeURIComponent(billerMatch[1]);

  // Lista de endpoints que NO deben recibir headers de tenant
  const excludedEndpoints = [
    '/payments/hierarchy/publishers',
    '/payments/hierarchy/billers',
    '/admin/publishers',
    '/admin/tenants',
    '/admin/users',
    '/permissions/',
    '/auth/'
  ];
  if (requestUrl && excludedEndpoints.some(endpoint => requestUrl.includes(endpoint))) return {};

  // Obtener publisherId desde múltiples fuentes
  let publisherId = null;
  // 1. Intentar desde userData del usuario
  const userData = localStorage.getItem(USER_DATA_KEY);
  if (userData) {
    try {
      const user = JSON.parse(userData);
      publisherId = user.assigned_publisher_id || user.publisher_id;
    } catch (e) {
      // Ignorar errores de parsing
    }
  }
  // 2. Si no hay publisherId del usuario, buscar en el tenant context guardado
  if (!publisherId) {
    try {
      const tenantContext = sessionStorage.getItem('ACMW_TENANT_CONTEXT');
      if (tenantContext) publisherId = JSON.parse(tenantContext).publisherId;
    } catch (e) {
      // Ignorar errores de parsing
    }
  }
  return publisherId && billerId ? { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId } : {};
};

// Interceptor para requests

api.interceptors.request.use(

  (config) => {


    // Auth token is sent automatically via httpOnly cookie (withCredentials: true)

    // Agregar X-Tab-ID para que el session cache funcione correctamente
    const tabId = getTabId();
    if (tabId) {
      config.headers['X-Tab-ID'] = tabId;
    }

    // Agregar headers de tenant context automáticamente desde la URL (ver getTenantHeaders)
    const tenantHeaders = getTenantHeaders(config.url);
    for (const [k, v] of Object.entries(tenantHeaders)) {
      // Solo agregar si no están ya presentes en los headers
      if (!config.headers[k]) config.headers[k] = v;
    }

    return config;

  },

  (error) => {

    console.error('❌ Error en request:', error);

    return Promise.reject(error);

  }

);


// Interceptor para responses
// Note: Global 401 handling is now done in axiosConfig.js via setupAxiosInterceptors
// This interceptor only handles logging and non-401 errors

api.interceptors.response.use(

  (response) => {


    return response;

  },

  (error) => {

    console.error('❌ Error en response:', error);

    // 401 errors are handled globally by axiosConfig.js interceptor
    // Don't handle them here to avoid duplicate redirects
    if (error.response?.status === HTTP_UNAUTHORIZED) {
      // Let the global interceptor handle this
      return Promise.reject(error);
    }

    // Silenciar errores en endpoints de SSE/import que se reconectan automáticamente
    const url = error.config?.url || '';
    const status = error.response?.status;
    const isGatewayError = status === 502 || status === 503 || status === 504;
    const isImportEndpoint = url.includes('/import/progress') || url.includes('/sse');

    if (isGatewayError && isImportEndpoint) {
      // SSE/polling reconecta automáticamente, no mostrar toast molesto
      return Promise.reject(error);
    }

    // Silenciar errores 404 en progress status endpoint - polling handles retries
    if (status === 404 && url.includes('/import/progress/') && url.includes('/status')) {
      // Progress session may not exist yet, polling will retry
      return Promise.reject(error);
    }

    // Silenciar errores 403 en endpoint de theme - ThemeContext usa cache/default como fallback
    if (status === 403 && url.includes('/theme')) {
      return Promise.reject(error);
    }

    // Silenciar 4xx en /file-converters/detect — el caller siempre maneja
    // la falla localmente (fallback al modo legacy / mensajes contextuales).
    if (status >= 400 && status < 500 && url.includes('/file-converters/detect')) {
      return Promise.reject(error);
    }

    // Mostrar error al usuario (solo para errores no-401 y no-SSE-504)

    const message = error.response?.data?.message || error.message || i18n.t('common:connectionError');

    toast.error(message);



    return Promise.reject(error);

  }

);


// API de pagos

export const paymentsAPI = {

  // Obtener todos los pagos

  getPayments: (params = {}) => {

    return api.get('/payments', { params });

  },


  // Obtener un pago específico

  getPayment: (paymentReference, publisherId, billerId) => {
    // Si se proporcionan publisherId y billerId, usar el endpoint multi-tenant
    if (publisherId && billerId) {
      return api.get(`/payments/${paymentReference}`, {
        headers: {
          'X-Publisher-ID': publisherId,
          'X-Biller-ID': billerId
        }
      });
    }

    // Fallback para compatibilidad
    return api.get(`/payments/${paymentReference}`);
  },


  // Crear nuevo pago

  createPayment: (data) => {

    return api.post('/payments', data);

  },


  // Marcar pago como completado

  markAsPaid: (paymentReference, data) => {

    return api.patch(`/payments/${paymentReference}/mark-paid`, data);

  },


  // Enviar notificación manual

  sendNotification: (paymentReference, templateType, publisherId, billerId) => {
    const headers = {};
    if (publisherId) headers['X-Publisher-ID'] = publisherId;
    if (billerId) headers['X-Biller-ID'] = billerId;
    return api.post(`/payments/${paymentReference}/send-notification`, { channel: 'email', templateType }, { headers });
  },

  // === NUEVAS FUNCIONES PARA LAZY LOADING ===

  // Obtener solo Publishers con totales
  getPublishers: () => {
    return api.get('/payments/hierarchy/publishers');
  },

  // Obtener Billers de un Publisher específico
  getBillersByPublisher: (publisherId) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers`);
  },

  // Obtener SOLO los lotes de un Publisher-Biller específico (sin pagos)
  getBatchesByPublisherBiller: (publisherId, billerId) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/batches`);
  },

  // Obtener pagos de un Publisher-Biller específico con paginación
  getPaymentsByPublisherBiller: (publisherId, billerId, params = {}) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/payments`, { params });
  },

  // Opciones del filtro "Adquirente" de Lotes → Buscar
  getPaymentAcquirerOptions: (publisherId, billerId) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/payments/acquirers`);
  },

  // === Cobertura de conciliación por lote (módulo Lotes) ===
  // Mapa de % por lote (columna "% Liq.")
  getReconciliationCoverage: (publisherId, billerId) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/coverage`);
  },
  // Detalle de conciliación de un lote (modal)
  getReconciliationByBatch: (publisherId, billerId, batch) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch`, { params: { batch } });
  },
  // Excel factura-a-factura del lote
  exportReconciliationByBatch: (publisherId, billerId, batch) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/export`, { params: { batch }, responseType: 'blob' });
  },
  // Cobros del lote aún no liquidados
  getReconciliationUnsettled: (publisherId, billerId, batch) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/unsettled`, { params: { batch } });
  },
  // Excel de los cobros no liquidados del lote
  exportReconciliationUnsettled: (publisherId, billerId, batch) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/unsettled/export`, { params: { batch }, responseType: 'blob' });
  },
  // Facturas del lote no conciliadas contra cobros (lista + categorías: sin movimiento / con intentos rechazados)
  getReconciliationUnreconciledInvoices: (publisherId, billerId, batch) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/unreconciled-invoices`, { params: { batch } });
  },
  // Excel de las facturas del lote no conciliadas contra cobros
  exportReconciliationUnreconciledInvoices: (publisherId, billerId, batch) => {
    return api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/unreconciled-invoices/export`, { params: { batch }, responseType: 'blob' });
  },
  // Devoluciones que confirma la liquidación (Amex): cerrar las no liquidadas del lote / deshacer el cierre
  closeUnconfirmedRefunds: (publisherId, billerId, batch) => {
    return api.post(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/refunds/close`, { batch });
  },
  reopenClosedRefunds: (publisherId, billerId, batch) => {
    return api.post(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/batch/refunds/reopen`, { batch });
  },
  // Cobros sin matchear sobre un conjunto de lotes (scope = lotes listados)
  // Opciones de los filtros del modal "Cobros sin matchear". Endpoint propio y
  // barato (catálogo de comercios): el modal las pide al abrirse, SIN disparar la
  // consulta de cobros, que es la que cuesta.
  getReconciliationUnmatchedOptions: (publisherId, billerId) =>
    api.get(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/unmatched/filter-options`),
  getReconciliationUnmatched: (publisherId, billerId, body) => {
    return api.post(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/unmatched`, body);
  },
  exportReconciliationUnmatched: (publisherId, billerId, body) => {
    return api.post(`/payments/hierarchy/publishers/${publisherId}/billers/${billerId}/reconciliation/unmatched/export`, body, { responseType: 'blob' });
  },

  // Obtener información detallada del lote para eliminación
  getBatchInfo: (batchId) => {
    return api.get(`/payments/batch/${encodeURIComponent(batchId)}/info`);
  },


  // Actualizar fecha de vencimiento
  updateDueDate: (paymentReference, dueDate) => {
    return api.patch(`/payments/${paymentReference}/due-date`, { due_date: dueDate });
  },

  // Obtener datos públicos de un pago (sin autenticación)
  getPublicPayment: (paymentReference) => {
    return api.get(`/public/payments/${paymentReference}`);
  },

  // Marcar pago como pagado desde página pública
  markAsPublicPaid: (paymentReference, data = {}) => {
    return api.patch(`/public/payments/${paymentReference}/mark-paid`, data);
  },

  // === NUEVAS FUNCIONES PARA PAGOS PARCIALES ===

  // Crear pago parcial público. Idempotency-Key es obligatorio en el backend:
  // el llamador genera una clave por intento de pago (crypto.randomUUID()) y la
  // reusa en los reintentos de ese mismo intento, para no duplicar el cobro.
  createPublicPartialPayment: (paymentReference, data, idempotencyKey) => {
    return api.post(`/public/payments/${paymentReference}/pay-partial`, data, {
      headers: { 'Idempotency-Key': idempotencyKey }
    });
  },

  // Obtener historial de transacciones público
  getPublicPaymentTransactions: (paymentReference) => {
    return api.get(`/public/payments/${paymentReference}/transactions`);
  },

  // Obtener balance de pago público
  getPublicPaymentBalance: (paymentReference) => {
    return api.get(`/public/payments/${paymentReference}/balance`);
  },

  // Crear pago parcial (autenticado)
  createPartialPayment: (paymentReference, data) => {
    return api.post(`/payments/${paymentReference}/transactions`, data);
  },

  // Obtener transacciones de pago (autenticado)
  // reminderId acota el historial a UNA factura: la referencia sola ya no identifica
  // una, dos presentaciones pueden compartirla.
  getPaymentTransactions: (paymentReference, publisherId, billerId, reminderId = null) => {
    const headers = {};
    if (publisherId) headers['X-Publisher-ID'] = publisherId;
    if (billerId) headers['X-Biller-ID'] = billerId;
    const params = reminderId ? { reminder_id: reminderId } : {};
    return api.get(`/payments/${paymentReference}/transactions`, { headers, params });
  },

  // Obtener balance de pago (autenticado)
  getPaymentBalance: (paymentReference) => {
    return api.get(`/payments/${paymentReference}/balance`);
  },

  // Validar pago parcial
  validatePartialPayment: (paymentReference, data) => {
    return api.post(`/payments/${paymentReference}/validate-partial`, data);
  },

  // Reversar transacción
  reverseTransaction: (transactionId, data) => {
    return api.put(`/payments/transactions/${transactionId}/reverse`, data);
  },

  // Obtener estadísticas de pagos parciales
  getPartialPaymentsStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/payments/stats/partial-payments${queryString ? '?' + queryString : ''}`);
  }

};


// API de configuración

export const configAPI = {

  // Obtener configuración de recordatorios

  getReminderConfig: () => {

    return api.get('/payments/config/reminders');

  },


  // Actualizar configuración

  updateReminderConfig: (id, data) => {

    return api.put(`/payments/config/reminders/${id}`, data);

  }

};


// API de estadísticas

export const statsAPI = {

  // Obtener estadísticas del dashboard

  getDashboardStats: () => {

    return api.get('/payments/dashboard/stats');

  },

  // Obtener estadísticas de plataforma (admin)
  getPlatformStats: () => {
    return api.get('/admin/platform/stats');
  },

  // Obtener evolución mensual de plataforma
  getPlatformEvolution: (params = {}) => {
    return api.get('/admin/platform/evolution', { params });
  },

  // Obtener información de versión del sistema
  getVersionInfo: () => {
    return api.get('/version');
  },


  // Obtener historial de notificaciones

  getNotificationHistory: (params = {}) => {

    return api.get('/payments/notifications/history', { params });

  },


  // Obtener estadísticas de notificaciones
  getNotificationStats: (params = {}) => {
    return api.get('/payments/notifications/stats', { params });
  },

  // Eliminar notificación individual
  deleteNotification: (notificationId) => {
    return api.delete(`/payments/notifications/${notificationId}`);
  },

  // Eliminar todas las notificaciones (con filtros opcionales)
  deleteAllNotifications: (params = {}) => {
    return api.delete('/payments/notifications', { params });
  }

};


// API de health check

export const healthAPI = {

  checkHealth: () => {

    return axios.get(`${API_BASE_URL.replace('/api', '')}/health`);

  }

};


// API de autenticación
export const authAPI = {
  // Registro de usuario
  register: (data) => {
    return api.post('/auth/register', data);
  },

  // Verificar email
  verifyEmail: (data) => {
    return api.post('/auth/verify-email', data);
  },

  // Login
  login: (data) => {
    return api.post('/auth/login', data);
  },

  // Logout
  logout: () => {
    return api.post('/auth/logout');
  },

  // Forgot password
  forgotPassword: (data) => {
    return api.post('/auth/forgot-password', data);
  },

  // Reset password
  resetPassword: (data) => {
    return api.post('/auth/reset-password', data);
  },

  // Verificar token
  verifyToken: () => {
    return api.get('/auth/verify-token');
  },

  // Actualizar perfil
  updateProfile: (data) => {
    return api.put('/auth/profile', data);
  },

  // Cambiar contraseña
  changePassword: (data) => {
    return api.post('/auth/change-password', data);
  },

  // Obtener T&C aceptados por el usuario
  getMyAcceptedTc: () => {
    return api.get('/auth/my-accepted-tc');
  },

  // Aceptar T&C pendientes
  acceptTc: (versionIds) => {
    return api.post('/auth/accept-tc', { version_ids: versionIds });
  },

  // Marcar novedad como vista
  markAnnouncementViewed: (announcementId) => {
    return api.post('/auth/mark-announcement-viewed', { announcement_id: announcementId });
  }
};

// API de Password Policies
export const passwordPoliciesAPI = {
  getActivePolicy: () => api.get('/password-policies/active'),
  getPolicy: () => api.get('/password-policies'),
  updatePolicy: (data) => api.put('/password-policies', data),
  getHistory: (params) => api.get('/password-policies/history', { params }),
};

// API de T&C
export const tcAPI = {
  getRoles: () => api.get('/tc/roles'),
  getDocuments: () => api.get('/tc/documents'),
  getStats: () => api.get('/tc/stats'),
  createDocument: (data) => api.post('/tc/documents', data),
  updateDocument: (id, data) => api.put(`/tc/documents/${id}`, data),
  getVersions: (docId) => api.get(`/tc/documents/${docId}/versions`),
  createVersion: (docId, formData) => api.post(`/tc/documents/${docId}/versions`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateVersion: (versionId, data) => api.put(`/tc/versions/${versionId}`, data),
  getVersionViewUrl: (versionId) => `/tc/versions/${versionId}/view`,
  publishVersion: (versionId) => api.post(`/tc/versions/${versionId}/publish`),
  getVersionAcceptances: (versionId, params = {}) => api.get(`/tc/versions/${versionId}/acceptances`, { params }),
  deleteVersion: (versionId) => api.delete(`/tc/versions/${versionId}`),
  getVersionFiles: (versionId) => api.get(`/tc/versions/${versionId}/files`),
  uploadVersionFile: (versionId, formData) => api.post(`/tc/versions/${versionId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteVersionFile: (versionId, language) => api.delete(`/tc/versions/${versionId}/files/${language}`),
  deleteDocument: (docId) => api.delete(`/tc/documents/${docId}`),
  getAuditLog: (params = {}) => api.get('/tc/audit', { params }),
  getVersionAcceptanceStats: (versionId) => api.get(`/tc/versions/${versionId}/acceptance-stats`),
};

// API de Novedades (Announcements)
export const announcementsAPI = {
  getAll: () => api.get('/announcements'),
  getStats: () => api.get('/announcements/stats'),
  getRoles: () => api.get('/announcements/roles'),
  create: (formData) => api.post('/announcements', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/announcements/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  publish: (id) => api.post(`/announcements/${id}/publish`),
  archive: (id) => api.post(`/announcements/${id}/archive`),
  delete: (id) => api.delete(`/announcements/${id}`),
  getImageUrl: (id) => `${API_BASE_URL}/announcements/${id}/image`,
  getViewStats: (id) => api.get(`/announcements/${id}/view-stats`),
  getViews: (id, params = {}) => api.get(`/announcements/${id}/views`, { params }),
  getAuditLog: (params = {}) => api.get('/announcements/audit/log', { params }),
};

// API de usuarios
export const usersAPI = {
  // Perfil del usuario actual
  getProfile: () => {
    return api.get('/users/profile');
  },

  // Actualizar perfil
  updateProfile: (data) => {
    return api.put('/users/profile', data);
  },

  // Admin: Obtener todos los usuarios
  getUsers: (params = {}) => {
    return api.get('/users', { params });
  },

  // Admin: Obtener usuario por ID
  getUserById: (id) => {
    return api.get(`/users/${id}`);
  },

  // Admin: Crear usuario
  createUser: (data) => {
    return api.post('/users', data);
  },

  // Admin: Actualizar usuario
  updateUser: (id, data) => {
    return api.put(`/users/${id}`, data);
  },

  // Admin: Eliminar usuario
  deleteUser: (id) => {
    return api.delete(`/users/${id}`);
  },

  // Admin: Reenviar email de verificación
  resendVerification: (id) => {
    return api.post(`/users/${id}/resend-verification`);
  },

  // Admin: Suspender/Activar usuario
  toggleUserStatus: (id, data) => {
    return api.patch(`/users/${id}/toggle-status`, data);
  },

  // Admin: Obtener log de actividad del sistema
  getActivityLog: (params = {}) => {
    return api.get('/users/system/activity-log', { params });
  },

  // Admin: Descargar log de actividad en Excel
  downloadActivityLog: (params = {}) => {
    return api.get('/users/system/activity-log/download', { 
      params,
      responseType: 'blob'
    });
  }
};

export default api;

// API de IA-Exchange (Platform-only)
export const iaExchangeAPI = {
  // Obtener lista de documentos con paginación y ordenamiento
  getDocuments: (params = {}) => {
    return api.get('/ia-exchange', { params });
  },

  // Obtener documento específico
  getDocument: (id) => {
    return api.get(`/ia-exchange/${id}`);
  },

  // Subir documento
  uploadDocument: (formData) => {
    return api.post('/ia-exchange/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Crear documento para descarga (usado por Claude Code)
  createForDownload: (data) => {
    return api.post('/ia-exchange/create-for-download', data);
  },

  // Descargar documento
  downloadDocument: (id) => {
    return api.get(`/ia-exchange/${id}/download`, {
      responseType: 'blob'
    });
  },

  // Obtener contenido del documento como texto
  getDocumentContent: (id) => {
    return api.get(`/ia-exchange/${id}/content`);
  },

  // Eliminar documento
  deleteDocument: (id) => {
    return api.delete(`/ia-exchange/${id}`);
  },

  // Obtener historial del documento
  getDocumentHistory: (id) => {
    return api.get(`/ia-exchange/${id}/history`);
  },

  // Obtener estadísticas
  getStats: () => {
    return api.get('/ia-exchange/stats');
  }
};


// API de templates
export const templatesAPI = {

  // Obtener todos los templates
  getAll: (params = {}) => {
    const config = {};

    // Si hay publisher_id y biller_id, enviarlos como headers para contexto de tenant
    if (params.publisher_id && params.biller_id) {
      config.headers = {
        'X-Publisher-ID': params.publisher_id,
        'X-Biller-ID': params.biller_id
      };
      // Remover de params para no duplicar en query string
      const { publisher_id, biller_id, ...restParams } = params;
      const queryString = new URLSearchParams(restParams).toString();
      return api.get(`/templates${queryString ? `?${queryString}` : ''}`, config);
    }

    const queryString = new URLSearchParams(params).toString();
    return api.get(`/templates${queryString ? `?${queryString}` : ''}`);
  },

  // Obtener template por ID
  getById: (templateId) => {
    return api.get(`/templates/${templateId}`);
  },

  // Obtener templates por biller
  getByBiller: (billerId) => {
    return api.get(`/templates/biller/${billerId}`);
  },

  // Obtener template específico por biller, canal y tipo
  getTemplate: (billerId, channel, templateType) => {
    const params = new URLSearchParams();
    if (billerId) params.append('biller_id', billerId);
    params.append('channel', channel);
    params.append('template_type', templateType);
    
    return api.get(`/templates/search?${params.toString()}`);
  },

  // Crear template
  create: (templateData) => {
    return api.post('/templates', templateData);
  },

  // Actualizar template
  update: (templateId, templateData) => {
    return api.put(`/templates/${templateId}`, templateData);
  },

  // Eliminar template
  delete: (templateId, options = {}) => {
    const config = {};
    if (options.publisherId && options.billerId) {
      config.headers = {
        'X-Publisher-ID': options.publisherId,
        'X-Biller-ID': options.billerId
      };
    }
    return api.delete(`/templates/${templateId}`, config);
  },

  // Obtener templates default
  getDefaults: () => {
    return api.get('/templates/defaults');
  },

  // Preview template con datos de ejemplo
  preview: (templateData, sampleData = {}) => {
    return api.post('/templates/preview', {
      template: templateData,
      sample_data: sampleData
    });
  },

  // Enviar email de prueba
  sendTest: (templateData, testEmail, sampleData = {}) => {
    return api.post('/templates/send-test', {
      template: templateData,
      test_email: testEmail,
      sample_data: sampleData
    });
  },

  // Obtener tipos de template disponibles
  getTypes: () => {
    return api.get('/templates/types');
  },

  // Obtener historial de cambios de un template
  getHistory: (templateId) => {
    return api.get(`/templates/${templateId}/history`);
  },

  // Obtener una versión específica del historial
  getHistoryVersion: (templateId, version) => {
    return api.get(`/templates/${templateId}/history/${version}`);
  }
};

// API de tipos de template
export const templateTypesAPI = {
  
  // Obtener todos los tipos de template
  getAll: (params = {}) => {
    return api.get('/template-types', { params });
  },

  // Obtener tipo de template por ID
  getById: (typeId) => {
    return api.get(`/template-types/${typeId}`);
  },

  // Obtener tipos activos (para dropdowns)
  getActive: (params = {}) => {
    return api.get('/template-types/active', { params });
  },

  // Obtener tipos para un biller específico (con fallback)
  getForBiller: (billerId) => {
    return api.get(`/template-types/biller/${billerId}`);
  },

  // Crear nuevo tipo de template
  create: (typeData) => {
    return api.post('/template-types', typeData);
  },

  // Actualizar tipo de template
  update: (typeId, typeData) => {
    return api.put(`/template-types/${typeId}`, typeData);
  },

  // Eliminar tipo de template
  delete: (typeId) => {
    return api.delete(`/template-types/${typeId}`);
  }
};

// API de vectores de notificación
export const notificationVectorsAPI = {
  
  // === Funciones administrativas ===

  // Obtener todos los vectores de notificación (admin)
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/notification-vectors/admin?${queryString}` : '/notification-vectors/admin';
    return api.get(url);
  },

  // Obtener vector por ID (admin)
  getById: (vectorId) => {
    return api.get(`/notification-vectors/admin/${vectorId}`);
  },

  // Crear vector (admin)
  create: (vectorData) => {
    return api.post('/notification-vectors/admin', vectorData);
  },

  // Actualizar vector (admin)
  update: (vectorId, vectorData) => {
    return api.put(`/notification-vectors/admin/${vectorId}`, vectorData);
  },

  // Eliminar vector (admin)
  delete: (vectorId) => {
    return api.delete(`/notification-vectors/admin/${vectorId}`);
  },

  // === Funciones para usuarios biller ===

  // Obtener todos los vectores (biller context)
  getAllBiller: (params = {}, publisherId, billerId) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/notification-vectors?${queryString}` : '/notification-vectors';
    return api.get(url, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener vector por ID (biller context)
  getByIdBiller: (vectorId, publisherId, billerId) => {
    return api.get(`/notification-vectors/${vectorId}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Crear vector (biller context)
  createBiller: (vectorData, publisherId, billerId) => {
    return api.post('/notification-vectors', vectorData, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Actualizar vector (biller context)
  updateBiller: (vectorId, vectorData, publisherId, billerId) => {
    return api.put(`/notification-vectors/${vectorId}`, vectorData, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Eliminar vector (biller context)
  deleteBiller: (vectorId, publisherId, billerId) => {
    return api.delete(`/notification-vectors/${vectorId}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener changelog de un plan (admin)
  getChangelog: (vectorId) => {
    return api.get(`/notification-vectors/admin/${vectorId}/changelog`);
  },

  // Obtener changelog de un plan (biller context)
  getChangelogBiller: (vectorId, publisherId, billerId) => {
    return api.get(`/notification-vectors/${vectorId}/changelog`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener opciones para dropdowns
  getOptions: () => {
    return api.get('/notification-vectors/admin/options/all');
  },

  // Obtener billers de un publisher específico
  getBillersByPublisher: (publisherId) => {
    // Pass publisher_id via headers (not query params for security)
    return api.get('/notification-vectors/admin/billers-by-publisher', {
      headers: {
        'X-Publisher-ID': publisherId
      }
    });
  },

  // === Funciones para API externa ===

  // Obtener vectores disponibles para asignar
  getAvailable: () => {
    return api.get('/notification-vectors/available');
  },

  // === Funciones VIEW-ONLY (no requieren admin, solo permisos) ===

  // Obtener todos los vectores (view-only para usuarios no admin)
  getAllViewOnly: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/notification-vectors?${queryString}` : '/notification-vectors';
    return api.get(url);
  },

  // Obtener opciones para dropdowns (view-only)
  getOptionsViewOnly: () => {
    return api.get('/notification-vectors/options');
  },

  // Obtener vector por ID (view-only)
  getByIdViewOnly: (vectorId) => {
    return api.get(`/notification-vectors/${vectorId}`);
  }
};

// API de configuraciones de billers
export const billerConfigurationsAPI = {
  
  // === Funciones administrativas ===
  
  // Obtener vectores de notificación disponibles
  getAvailableVectors: () => {
    return api.get('/biller-configurations/admin/available-vectors');
  },
  
  // Obtener todas las configuraciones de billers
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/biller-configurations/admin?${queryString}` : '/biller-configurations/admin';
    return api.get(url);
  },

  // Obtener configuración específica
  getByBiller: (publisherSystemId, billerId) => {
    return api.get(`/biller-configurations/admin/${publisherSystemId}/${billerId}`);
  },

  // Crear configuración
  create: (configData) => {
    return api.post('/biller-configurations/admin', configData);
  },

  // Actualizar configuración
  update: (publisherSystemId, billerId, configData) => {
    return api.put(`/biller-configurations/admin/${publisherSystemId}/${billerId}`, configData);
  },

  // Eliminar configuración
  delete: (publisherSystemId, billerId) => {
    return api.delete(`/biller-configurations/admin/${publisherSystemId}/${billerId}`);
  },

  // === Funciones para API externa ===
  
  // Obtener configuración de notificaciones para un biller
  getNotificationConfig: (publisherSystemId, billerId) => {
    return api.get(`/biller-configurations/${publisherSystemId}/${billerId}/notification-config`);
  },

  // Crear configuración desde API externa
  createExternal: (configData) => {
    return api.post('/biller-configurations/external', configData);
  },

  // Actualizar desde API externa
  updateExternal: (publisherSystemId, billerId, configData) => {
    return api.put(`/biller-configurations/${publisherSystemId}/${billerId}/external`, configData);
  }
};

// API de billers
export const billersAPI = {
  
  // === Funciones para templates (originales) ===
  
  // Obtener todos los billers (lista simple para templates)
  getList: () => {
    return api.get('/billers/list');
  },

  // === Funciones administrativas (nueva funcionalidad) ===
  
  // Obtener vectores de notificación disponibles
  getAvailableVectors: () => {
    return api.get('/billers/admin/available-vectors');
  },
  
  // Obtener todos los billers con paginación y filtros (admin)
  getAll: (queryString = '') => {
    const url = queryString ? `/billers/admin?${queryString}` : '/billers/admin';
    return api.get(url);
  },

  // Actividad (facturas, cobros, clientes, lotes...) de un conjunto de billers
  // en una sola request. refresh=true ignora el cache de 5 min del backend.
  getActivity: (billerIds = [], { refresh = false } = {}) => {
    const params = new URLSearchParams({ ids: billerIds.join(',') });
    if (refresh) params.set('refresh', '1');
    return api.get(`/billers/admin/activity?${params.toString()}`);
  },

  // Obtener billers de un sistema específico (con paginación y búsqueda opcionales)
  getBySystem: (systemId, { page, limit, search } = {}) => {
    const params = new URLSearchParams({ publisher_system_id: String(systemId) });
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(limit));
    if (search) params.set('search', search);
    return api.get(`/billers/admin?${params.toString()}`);
  },

  // Obtener biller por ID
  getById: (billerId) => {
    return api.get(`/billers/${billerId}`);
  },

  // Crear biller manualmente (admin)
  create: (billerData) => {
    return api.post('/billers/admin', billerData);
  },

  // Listado de empresas del canal con su detalle: format 'pdf' | 'xlsx' (doc/50 §10)
  exportBySystem: (systemId, format, { search } = {}) => api.get('/billers/admin/export', {
    params: { publisher_system_id: systemId, format, search: search || undefined },
    responseType: 'blob',
  }),

  // Configuración de alta de la empresa en JSON, para crearla en otro ambiente (doc/50 §11)
  exportConfig: (billerPk) => api.get(`/billers/admin/${billerPk}/export-config`, { responseType: 'blob' }),
  // Resuelve el JSON contra este ambiente y devuelve el alta precargada (no crea nada)
  previewImportConfig: (systemId, data) => api.post('/billers/admin/import-config/preview', { publisher_system_id: systemId, data }),

  // Datos adicionales de la empresa, por país (doc/50). billerPk = publisher_system_billers.id
  getCompanyFields: (billerPk) => api.get(`/billers/admin/${billerPk}/company-fields`),
  saveCompanyFields: (billerPk, values) => api.put(`/billers/admin/${billerPk}/company-fields`, { values }),
  getCompanyFieldsAudit: (billerPk) => api.get(`/billers/admin/${billerPk}/company-fields/audit`),

  // Actualizar biller (admin)
  update: (billerId, billerData) => {
    return api.put(`/billers/admin/${billerId}`, billerData);
  },

  // Eliminar biller (admin)
  delete: (billerId) => {
    return api.delete(`/billers/admin/${billerId}`);
  },

  // Eliminar biller con confirmación (admin)
  deleteWithConfirmation: (billerId, confirmationData) => {
    return api.delete(`/billers/admin/${billerId}`, { data: confirmationData });
  },

  // Obtener lotes disponibles para análisis
  getAvailableBatches: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/batch-summary`);
  },

  // Obtener años disponibles para análisis de KPIs
  getAvailableYears: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/available-years`);
  },

  // Obtener análisis de medios de pago
  // dateRange = { from, to } (YYYY-MM-DD) → modalidad calendario: scope por
  // fecha de cobro en vez de por lote. Si se pasa, ignora `batches`.
  getPaymentMethodsAnalysis: (billerId, batches, showAmounts = false, channel = null, segments = null, dateRange = null) => {
    let url = `/billers/${billerId}/dashboard/payment-methods-analysis?showAmounts=${showAmounts}`;
    if (dateRange && dateRange.from && dateRange.to) {
      url += `&date_from=${encodeURIComponent(dateRange.from)}&date_to=${encodeURIComponent(dateRange.to)}`;
    } else {
      const batchesParam = Array.isArray(batches) ? batches.join(',') : batches;
      url += `&batches=${encodeURIComponent(batchesParam)}`;
    }
    if (channel && channel !== 'all') {
      url += `&channel=${encodeURIComponent(channel)}`;
    }
    if (segments) {
      url += `&segments=${encodeURIComponent(segments)}`;
    }
    return api.get(url);
  },

  // Obtener apertura por tarjeta (adquirente / marca / rechazos) — solo cobros con tarjeta
  // allBatches=true agrega todos los lotes del biller (necesario para que el share
  // tenga sentido en DA, donde cada lote es la cobranza de un único adquirente)
  // Serie temporal de medios de pago por fecha de cobro (modalidad calendario).
  // opts: { date_from, date_to, group_by: 'channel'|'method', channel, segments }
  getPaymentMethodsTimeSeries: (billerId, opts = {}) => {
    const p = new URLSearchParams();
    if (opts.date_from) p.set('date_from', opts.date_from);
    if (opts.date_to) p.set('date_to', opts.date_to);
    p.set('group_by', opts.group_by === 'method' ? 'method' : 'channel');
    if (opts.channel && opts.channel !== 'all') p.set('channel', opts.channel);
    if (opts.segments) p.set('segments', opts.segments);
    return api.get(`/billers/${billerId}/dashboard/payment-methods-timeseries?${p.toString()}`);
  },

  getPaymentCardAnalysis: (billerId, batches, channel = null, segments = null, allBatches = false, dateRange = null) => {
    const params = [];
    if (dateRange && dateRange.from && dateRange.to) {
      params.push(`date_from=${encodeURIComponent(dateRange.from)}`);
      params.push(`date_to=${encodeURIComponent(dateRange.to)}`);
    } else if (allBatches) {
      params.push('allBatches=true');
    } else {
      const batchesParam = Array.isArray(batches) ? batches.join(',') : (batches || '');
      params.push(`batches=${encodeURIComponent(batchesParam)}`);
    }
    if (channel && channel !== 'all') {
      params.push(`channel=${encodeURIComponent(channel)}`);
    }
    if (segments) {
      params.push(`segments=${encodeURIComponent(segments)}`);
    }
    return api.get(`/billers/${billerId}/dashboard/payment-card-analysis?${params.join('&')}`);
  },

  // Obtener indicadores de cobranza (KPIs)
  getBillerCollectionKPIs: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/dashboard/collection-kpis${queryString ? `?${queryString}` : ''}`);
  },

  // Obtener score de cobranza por cliente
  getBillerCustomerScores: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/dashboard/customer-scores${queryString ? `?${queryString}` : ''}`);
  },

  // === Funciones de Collection Score Plan ===

  // Obtener scores de cobranza con filtros y paginación
  getCollectionScores: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores${queryString ? `?${queryString}` : ''}`);
  },

  // Calcular scores de cobranza
  calculateCollectionScores: (billerId, data = {}) => {
    return api.post(`/billers/${billerId}/collection-scores/calculate`, data);
  },

  // Obtener analytics de scores
  getCollectionScoreAnalytics: (billerId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/analytics${queryString ? `?${queryString}` : ''}`);
  },

  // Obtener configuraciones de score
  getCollectionScoreConfigs: (billerId) => {
    return api.get(`/billers/${billerId}/collection-scores/configs`);
  },

  // Crear configuración de score
  createCollectionScoreConfig: (billerId, config) => {
    return api.post(`/billers/${billerId}/collection-scores/configs`, config);
  },

  // Actualizar configuración de score
  updateCollectionScoreConfig: (billerId, configId, config) => {
    return api.put(`/billers/${billerId}/collection-scores/configs/${configId}`, config);
  },

  // Obtener score específico de un cliente
  getCustomerCollectionScore: (billerId, customerId) => {
    return api.get(`/billers/${billerId}/collection-scores/${customerId}`);
  },

  // Crear plan de cobranza personalizado
  createCollectionPlan: (billerId, customerId, plan) => {
    return api.post(`/billers/${billerId}/collection-scores/${customerId}/plan`, plan);
  },

  // === COLLECTION ALERTS & DASHBOARDS ===

  // Obtener alertas activas del biller
  getActiveAlerts: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Dashboard de alertas
  getAlertsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Dashboard de recomendaciones
  getRecommendationsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/recommendations${queryParams ? '?' + queryParams : ''}`);
  },

  // Resolver una alerta
  resolveAlert: (billerId, alertId, resolvedBy, resolvedNote = null) => {
    return api.put(`/billers/${billerId}/collection-scores/alerts/${alertId}/resolve`, {
      resolvedBy,
      resolvedNote
    });
  },

  // === ADVANCED CONFIGURATION ===

  // Obtener configuración avanzada
  getAdvancedConfig: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/config/advanced${queryParams ? '?' + queryParams : ''}`);
  },

  // Actualizar configuración avanzada
  updateAdvancedConfig: (billerId, configType, configData, publisherId = null) => {
    return api.put(`/billers/${billerId}/collection-scores/config/advanced/${configType}`, {
      publisherId,
      configData
    });
  },

  // Obtener análisis avanzado detallado de un cliente
  getAdvancedAnalysis: (billerId, customerEmail) => {
    // URL encode del email para manejar caracteres especiales
    const encodedEmail = encodeURIComponent(customerEmail);
    return api.get(`/billers/${billerId}/collection-scores/${encodedEmail}/advanced`);
  },

  // === INDUSTRY MANAGEMENT ===

  // Obtener todas las industrias
  getIndustries: (includeInactive = false) => {
    return api.get(`/billers/admin/industries${includeInactive ? '?include_inactive=1' : ''}`);
  },

  // Crear industria
  createIndustry: (data) => {
    return api.post('/billers/admin/industries', data);
  },

  // Actualizar industria
  updateIndustry: (id, data) => {
    return api.put(`/billers/admin/industries/${id}`, data);
  },

  // Eliminar industria
  deleteIndustry: (id) => {
    return api.delete(`/billers/admin/industries/${id}`);
  },

  // Importar industrias desde JSON
  importIndustries: (industries) => {
    return api.post('/billers/admin/industries/import', { industries });
  },

  // Obtener billers con industrias asignadas
  getBillersWithIndustries: () => {
    return api.get('/billers/admin/with-industries');
  },

  // Actualizar industria de un biller
  updateBillerIndustry: (billerId, data) => {
    return api.put(`/billers/admin/${billerId}/industry`, data);
  },

  // Asignación masiva automática de industrias
  bulkAssignIndustries: () => {
    return api.post('/billers/admin/industries/bulk-assign');
  },

  // === API KEY MANAGEMENT (v3.3.0) ===

  // Obtener información de API Key de un biller (enmascarada)
  getApiKeyInfo: (billerId) => {
    return api.get(`/billers/admin/${billerId}/api-key`);
  },

  // Generar nueva API Key para un biller
  generateApiKey: (billerId) => {
    return api.post(`/billers/admin/${billerId}/api-key/generate`);
  },

  // Regenerar (rotar) API Key de un biller
  regenerateApiKey: (billerId) => {
    return api.post(`/billers/admin/${billerId}/api-key/regenerate`, { confirm: true });
  },

  // Revocar API Key de un biller
  revokeApiKey: (billerId) => {
    return api.delete(`/billers/admin/${billerId}/api-key`, { data: { confirm: true } });
  },

  // === LOGO MANAGEMENT ===

  // Subir logo de un biller
  uploadLogo: (billerId, formData, config) => {
    return api.post(`/billers/admin/${billerId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    });
  },

  // Eliminar logo de un biller
  deleteLogo: (billerId) => {
    return api.delete(`/billers/admin/${billerId}/logo`);
  }
};

// API de sistemas externos
export const publisherSystemsAPI = {
  
  // Obtener todos los sistemas externos
  getAll: () => {
    return api.get('/publisher-systems');
  },

  // Obtener sistema externo por ID
  getById: (systemId) => {
    return api.get(`/publisher-systems/${systemId}`);
  },

  // Crear nuevo sistema externo
  create: (systemData) => {
    return api.post('/publisher-systems', systemData);
  },

  // Actualizar sistema externo
  update: (systemId, systemData) => {
    return api.put(`/publisher-systems/${systemId}`, systemData);
  },

  // Eliminar sistema externo
  delete: (systemId) => {
    return api.delete(`/publisher-systems/${systemId}`);
  },

  // Regenerar API key
  regenerateApiKey: (systemId) => {
    return api.post(`/publisher-systems/${systemId}/regenerate-key`);
  },

  // Obtener billers de un publisher específico
  getBillers: (publisherId) => {
    return api.get(`/publisher-systems/${publisherId}/billers`);
  },

  // Obtener países de un publisher
  getCountries: (publisherId) => {
    return api.get(`/publisher-systems/${publisherId}/countries`);
  },

  // Obtener perfil del canal (países, programas, conteo empresas)
  getProfile: (publisherId) => {
    return api.get(`/publisher-systems/${publisherId}/profile`);
  },

  // Subir logo
  uploadLogo: (publisherId, formData, config) => {
    return api.post(`/publisher-systems/${publisherId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    });
  },

  // Eliminar logo
  deleteLogo: (publisherId) => {
    return api.delete(`/publisher-systems/${publisherId}/logo`);
  },

  // === Comisiones ===

  // Obtener balances de comisiones por país
  getCommissionBalances: (publisherId) => {
    return api.get(`/publisher-systems/${publisherId}/commissions/balances`);
  },

  // Obtener transacciones de comisiones
  getCommissionTransactions: (publisherId, params = {}) => {
    return api.get(`/publisher-systems/${publisherId}/commissions/transactions`, { params });
  },

  // Registrar pago de comisiones
  recordCommissionPayment: (publisherId, paymentData) => {
    return api.post(`/publisher-systems/${publisherId}/commissions/payment`, paymentData);
  },

  // Exportar cuenta de comisiones a PDF (blob)
  exportCommissionPDF: (publisherId, params = {}) => {
    return api.get(`/publisher-systems/${publisherId}/commissions/export-pdf`, { params, responseType: 'blob' });
  },

  // Obtener balances bulk para listado
  getBulkCommissionBalances: () => {
    return api.get('/publisher-systems/commissions/bulk-balances');
  }
};

// API de depuración de facturas
export const purgeAPI = {
  
  // Obtener estadísticas de depuración
  getStats: () => {
    return api.get('/purge/stats');
  },

  // Buscar facturas depuradas
  searchPurged: (filters) => {
    return api.get('/purge/search', { params: filters });
  },

  // Obtener candidatos para depuración
  getCandidates: (limit = 100) => {
    return api.get('/purge/candidates', { params: { limit } });
  },

  // Depurar factura específica
  purgePaymentReminder: (paymentId, reason) => {
    return api.post('/purge/payment-reminder', { payment_id: paymentId, reason });
  },

  // Depurar múltiples facturas
  purgeBatch: (paymentIds, reason) => {
    return api.post('/purge/batch', { payment_ids: paymentIds, reason });
  },

  // Ejecutar depuración automática manualmente
  runAutomaticPurge: (batchSize = 1000) => {
    return api.post('/purge/run-automatic', { batch_size: batchSize });
  }
};

// API de dashboard de biller
export const billerDashboardAPI = {
  
  // Obtener información general del biller
  getInfo: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/info`);
  },

  // Obtener estadísticas de facturas
  getStats: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/stats`);
  },

  // Obtener estadísticas de notificaciones
  getNotificationStats: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/notifications`);
  },
  // Obtener resumen por canal con emails leídos
  getChannelSummary: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/channel-summary`);
  },
  // Obtener resumen por lote
  getBatchSummary: (billerId) => {
    return api.get(`/billers/${billerId}/dashboard/batch-summary`);
  },

  // Obtener distribución de medios de pago por día
  getPaymentMethodsDistribution: (billerId, batch, channel = null, segments = null) => {
    let url = `/billers/${billerId}/dashboard/payment-methods?batch=${encodeURIComponent(batch)}`;
    if (channel && channel !== 'all') {
      url += `&channel=${encodeURIComponent(channel)}`;
    }
    if (segments) {
      url += `&segments=${encodeURIComponent(segments)}`;
    }
    return api.get(url);
  },

  // Obtener análisis de medios de pago
  // dateRange = { from, to } (YYYY-MM-DD) → modalidad calendario: scope por
  // fecha de cobro en vez de por lote. Si se pasa, ignora `batches`.
  getPaymentMethodsAnalysis: (billerId, batches, showAmounts = false, channel = null, segments = null, dateRange = null) => {
    let url = `/billers/${billerId}/dashboard/payment-methods-analysis?showAmounts=${showAmounts}`;
    if (dateRange && dateRange.from && dateRange.to) {
      url += `&date_from=${encodeURIComponent(dateRange.from)}&date_to=${encodeURIComponent(dateRange.to)}`;
    } else {
      const batchesParam = Array.isArray(batches) ? batches.join(',') : batches;
      url += `&batches=${encodeURIComponent(batchesParam)}`;
    }
    if (channel && channel !== 'all') {
      url += `&channel=${encodeURIComponent(channel)}`;
    }
    if (segments) {
      url += `&segments=${encodeURIComponent(segments)}`;
    }
    return api.get(url);
  },

  // Obtener evolución histórica de medios de pago (snapshots mensuales)
  getPaymentMethodsEvolution: (billerId, startMonth, endMonth, mode = 'amount', segments = null) => {
    let url = `/billers/${billerId}/payment-methods/evolution?startMonth=${startMonth}&endMonth=${endMonth}&mode=${mode}`;
    if (segments) {
      url += `&segments=${encodeURIComponent(segments)}`;
    }
    return api.get(url);
  },

  // Obtener años disponibles para gráfico de evolución de medios de pago
  getPaymentMethodsAvailableYears: (billerId) => {
    return api.get(`/billers/${billerId}/payment-methods/available-years`);
  },

  // Obtener evolución de facturación mensual por cuenta
  getBillingEvolution: (billerId, params = {}) => {
    const {
      months = 12,
      year = null, // Año específico para filtrar
      accountNumber = null, // Cuenta específica para filtrar
      page = 1,
      limit = 10,
      search = '',
      sortField = 'account_number',
      sortDirection = 'asc'
    } = params;

    const queryParams = new URLSearchParams({
      months: months.toString(),
      page: page.toString(),
      limit: limit.toString(),
      sortField,
      sortDirection
    });

    // Si hay un año específico, agregarlo a los parámetros
    if (year && year !== '') {
      queryParams.append('year', year.toString());
    }

    // Si hay una cuenta específica, agregarlo a los parámetros
    if (accountNumber && accountNumber.trim() !== '') {
      queryParams.append('accountNumber', accountNumber.trim());
    }

    if (search && search.trim() !== '') {
      queryParams.append('search', search.trim());
    }

    return api.get(`/billers/${encodeURIComponent(billerId)}/dashboard/billing-evolution?${queryParams.toString()}`);
  },

  // Obtener análisis de vencimientos (v3.9.0)
  getDueDateAnalysis: (billerId, params = {}) => {
    const { batch = null, startDate = null, endDate = null, year = null, onlyYears = false } = params;
    const queryParams = new URLSearchParams();

    if (onlyYears) {
      queryParams.append('onlyYears', 'true');
    } else {
      if (batch && batch !== 'all') {
        queryParams.append('batch', batch);
      }
      if (year && year !== '') {
        queryParams.append('year', year);
      }
      if (startDate) {
        queryParams.append('startDate', startDate);
      }
      if (endDate) {
        queryParams.append('endDate', endDate);
      }
    }

    const queryString = queryParams.toString();
    const url = `/billers/${encodeURIComponent(billerId)}/dashboard/due-date-analysis${queryString ? '?' + queryString : ''}`;
    return api.get(url);
  }
};

// API de canales
export const channelsAPI = {

  // Obtener todos los canales
  getAll: () => {
    return api.get('/channels');
  },

  // Obtener solo canales habilitados/configurados
  getEnabled: () => {
    return api.get('/channels/enabled');
  },

  // === GESTIÓN DE COLA DE EMAILS ===

  // Obtener estadísticas de la cola
  getEmailQueueStats: (publisherId, billerId) => {
    return api.get('/channels/email/queue/stats', {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener emails en cola
  getEmailQueue: (publisherId, billerId, params = {}) => {
    return api.get('/channels/email/queue', {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      },
      params
    });
  },

  // Vaciar cola de emails
  clearEmailQueue: (publisherId, billerId, status = null) => {
    return api.delete('/channels/email/queue', {
      data: { publisher_id: publisherId, biller_id: billerId, status }
    });
  },

  // Reintentar emails fallidos
  retryFailedEmails: (publisherId, billerId) => {
    return api.post('/channels/email/queue/retry-failed', {
      publisher_id: publisherId,
      biller_id: billerId
    });
  },

  // === COLA DE PLATAFORMA (v3.5.0) ===

  // Obtener estadísticas de la cola de plataforma
  getPlatformQueueStats: () => {
    return api.get('/channels/email/platform-queue/stats');
  },

  // Obtener emails de la cola de plataforma
  getPlatformQueue: (params = {}) => {
    return api.get('/channels/email/platform-queue', { params });
  },

  // Vaciar cola de plataforma
  clearPlatformQueue: (status = null) => {
    return api.delete('/channels/email/platform-queue', {
      data: { status }
    });
  },

  // Reintentar emails fallidos de plataforma
  retryPlatformFailedEmails: () => {
    return api.post('/channels/email/platform-queue/retry-failed');
  }
};

// API de precios
export const pricesAPI = {
  
  // Obtener todas las listas de precios
  getAll: (params = {}) => {
    return api.get('/prices', { params });
  },

  // Obtener lista de precios por ID
  getById: (priceListId) => {
    return api.get(`/prices/${priceListId}`);
  },

  // Crear nueva lista de precios
  create: (priceListData) => {
    return api.post('/prices', priceListData);
  },

  // Actualizar lista de precios
  update: (priceListId, priceListData) => {
    return api.put(`/prices/${priceListId}`, priceListData);
  },

  // Eliminar lista de precios
  delete: (priceListId) => {
    return api.delete(`/prices/${priceListId}`);
  },

  // Obtener asignaciones de publishers y billers
  getAssignments: () => {
    return api.get('/prices/assignments');
  },

  // Asignar lista a publisher (herencia a billers)
  assignPublisher: (publisherId, priceListId) => {
    return api.put(`/prices/assign-publisher/${publisherId}`, { price_list_id: priceListId });
  },

  // Asignar lista directamente a biller
  assignBiller: (billerId, priceListId) => {
    return api.put(`/prices/assign-biller/${billerId}`, { price_list_id: priceListId });
  }
};

// API de Medios (v3.12.0)
export const mediosAPI = {

  // Obtener estadísticas de canales de notificación
  getChannelStats: (range = 'this_month') => {
    return api.get('/medios/channel-stats', { params: { range } });
  }
};

// API de logs
export const logsAPI = {
  
  // Obtener archivos de logs disponibles
  getFiles: () => {
    return api.get('/logs/files');
  },

  // Obtener contenido de un log específico
  getLogContent: (logType, params = {}) => {
    return api.get(`/logs/${logType}`, { params });
  },

  // Limpiar archivo de log (admin only)
  clearLog: (logType) => {
    return api.delete(`/logs/${logType}`);
  }
};

// API de administración
export const adminAPI = {
  
  // Inicializar sistema (eliminar todos los datos)
  initializeSystem: () => {
    return api.post('/admin/initialize-system');
  },

  // Obtener estadísticas del sistema
  getSystemStats: () => {
    return api.get('/admin/system-stats');
  },

  // Listar todos los backups disponibles
  listBackups: () => {
    return api.get('/admin/backups');
  },

  // Restaurar desde un backup específico
  restoreFromBackup: (backupId) => {
    return api.post(`/admin/backups/${backupId}/restore`);
  },

  // Eliminar un backup específico
  deleteBackup: (backupId) => {
    return api.delete(`/admin/backups/${backupId}`);
  },

  // Crear backup manual
  createManualBackup: () => {
    return api.post('/admin/create-backup');
  },

  // Obtener configuración de parámetros de depuración
  getPurgeConfig: () => {
    return api.get('/admin/purge-config');
  },

  // Actualizar configuración de parámetros de depuración
  updatePurgeConfig: (configData) => {
    return api.put('/admin/purge-config', configData);
  },

  // Obtener vectores de notificación disponibles
  getAvailableVectors: () => {
    return api.get('/admin/available-vectors');
  },

  // Obtener billers disponibles
  getAvailableBillers: () => {
    return api.get('/admin/available-billers');
  },

  // Exportar dashboard a PDF
  exportDashboardToPDF: (billerId, options = {}) => {
    return api.post(`/billers/${billerId}/dashboard/export-pdf`, options);
  },

  // Exportar reporte ejecutivo a PDF
  exportExecutiveDashboardToPDF: (billerId, options = {}) => {
    return api.post(`/billers/${billerId}/dashboard/export-executive-pdf`, options);
  },

  // Obtener información de todos los tenants (desde caché)
  getTenants: () => {
    return api.get('/admin/tenants');
  },

  // Refrescar caché de todos los tenants
  refreshAllTenants: () => {
    return api.post('/admin/tenants/refresh-all');
  },

  // Refrescar caché de un tenant específico
  refreshSingleTenant: (tenantKey) => {
    return api.post(`/admin/tenants/${tenantKey}/refresh`);
  },

  // Obtener información de las bases de datos del sistema (Central y Model)
  getSystemDatabases: () => {
    return api.get('/admin/system-databases');
  }
};

// === API AVANZADA DE COLLECTION SCORES ===
export const advancedCollectionAPI = {


  // Análisis avanzado completo de un cliente
  getAdvancedAnalysis: (billerId, customerEmail, publisherId = null) => {
    // Pass publisherId via headers (not query params for security)
    const config = publisherId ? { headers: { 'X-Publisher-ID': publisherId } } : {};
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/advanced`, config);
  },

  // Obtener solo componentes de análisis
  getAnalysisComponents: (billerId, customerEmail, publisherId = null) => {
    // Pass publisherId via headers (not query params for security)
    const config = publisherId ? { headers: { 'X-Publisher-ID': publisherId } } : {};
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/analysis`, config);
  },

  // Obtener predicciones para un cliente
  getCustomerPredictions: (billerId, customerEmail, publisherId = null) => {
    // Pass publisherId via headers (not query params for security)
    const config = publisherId ? { headers: { 'X-Publisher-ID': publisherId } } : {};
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/predictions`, config);
  },

  // Obtener score avanzado existente
  getExistingAdvancedScore: (billerId, customerEmail, publisherId = null) => {
    // Pass publisherId via headers (not query params for security)
    const config = publisherId ? { headers: { 'X-Publisher-ID': publisherId } } : {};
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/score-advanced`, config);
  },

  // Obtener evolución de scores
  getScoreEvolution: (billerId, customerEmail, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/evolution${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener alertas activas del biller
  getActiveAlerts: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener alertas de un cliente específico
  getCustomerAlerts: (billerId, customerEmail, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/${customerEmail}/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Resolver una alerta
  resolveAlert: (billerId, alertId, resolvedBy, resolvedNote = null) => {
    return api.put(`/billers/${billerId}/collection-scores/alerts/${alertId}/resolve`, {
      resolvedBy,
      resolvedNote
    });
  },

  // Dashboard de alertas
  getAlertsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/alerts${queryParams ? '?' + queryParams : ''}`);
  },

  // Dashboard de recomendaciones
  getRecommendationsDashboard: (billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/billers/${billerId}/collection-scores/dashboard/recommendations${queryParams ? '?' + queryParams : ''}`);
  },

  // Recálculo masivo de scores avanzados
  recalculateAdvancedScores: (billerId, options = {}) => {
    return api.post(`/billers/${billerId}/collection-scores/recalculate-advanced`, options);
  },

  // Health check del sistema avanzado
  checkAdvancedSystemHealth: (billerId) => {
    return api.get(`/billers/${billerId}/collection-scores/health-check`);
  },

  // Testing endpoint para análisis
  testAdvancedAnalysis: (billerId, customerEmail, publisherId = null) => {
    // Pass publisherId via headers (not query params for security)
    const config = publisherId ? { headers: { 'X-Publisher-ID': publisherId } } : {};
    return api.get(`/billers/${billerId}/collection-scores/test-analysis/${customerEmail}`, config);
  }
};

// API de gestión de clientes
export const customersAPI = {

  // Listar todos los clientes con filtros
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/customers${queryParams ? '?' + queryParams : ''}`);
  },

  // Obtener cliente específico
  getById: (id) => {
    return api.get(`/customers/${id}`);
  },

  // Obtener clientes por biller específico
  getByBiller: (publisherSystemId, billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/customers/by-biller/${publisherSystemId}/${billerId}${queryParams ? '?' + queryParams : ''}`;
    // Pass publisherSystemId and billerId via headers for tenant context authorization
    return api.get(url, {
      headers: {
        'X-Publisher-ID': publisherSystemId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Crear nuevo cliente
  create: (customerData) => {
    return api.post('/customers', customerData);
  },

  // Actualizar cliente
  update: (id, customerData) => {
    return api.put(`/customers/${id}`, customerData);
  },

  // Eliminar cliente
  delete: (id) => {
    return api.delete(`/customers/${id}`);
  },

  // Exportar clientes a CSV
  exportCSV: (publisherSystemId, billerId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = `/customers/export/${publisherSystemId}/${billerId}${queryParams ? '?' + queryParams : ''}`;
    return api.get(url, {
      responseType: 'blob',
      headers: {
        'X-Publisher-ID': publisherSystemId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Eliminar todos los clientes de un publisher-biller
  massDelete: (publisherSystemId, billerId) => {
    return api.delete(`/customers/mass-delete/${publisherSystemId}/${billerId}`, {
      headers: {
        'X-Publisher-ID': publisherSystemId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener historial de pagos de un cliente por customer_id numérico
  getPaymentHistory: (customerId) => {
    return api.get(`/customers/${customerId}/payment-history`);
  },

  // Obtener historial de pagos por account_id (payer_id)
  getPaymentHistoryByAccount: (accountId) => {
    return api.get(`/customers/by-account/${accountId}/payment-history`);
  },

  // Obtener historial completo de pagos por email (incluye pagos completos y parciales)
  getCustomerPaymentHistoryByEmail: (customerEmail, billerId, publisherId = null) => {
    const url = `/v1/billers/customers/${encodeURIComponent(customerEmail)}/history`;
    // Pass publisherId and billerId via headers for tenant context
    const headers = {};
    if (publisherId) headers['X-Publisher-ID'] = publisherId;
    if (billerId) headers['X-Biller-ID'] = billerId;
    return api.get(url, { headers });
  },

  // Función auxiliar para obtener publisher_system_biller_id
  getPublisherSystemBillerId: async (publisherSystemId, billerId) => {
    try {
      const response = await api.get(`/billers/${billerId}/publisher-systems`);
      const publisherSystems = response.data?.data?.publisher_systems || response.data?.publisher_systems || [];
      const publisherBiller = publisherSystems.find(
        ps => ps.publisher_id === publisherSystemId || ps.publisher_system_id.toString() === publisherSystemId.toString()
      );

      if (!publisherBiller) {
        throw new Error(`Publisher System ${publisherSystemId} not found for Biller ${billerId}`);
      }

      return { publisher_system_biller_id: publisherBiller.id };
    } catch (error) {
      console.error('Error en getPublisherSystemBillerId:', error);
      throw error;
    }
  }
};

// API de schedulers (Admin)
export const schedulersAPI = {

  // Obtener información de todos los schedulers
  // v3.5.2: groupId opcional para obtener configuración específica de un grupo
  getSchedulers: (groupId = null) => {
    return api.get('/admin/schedulers', { params: groupId ? { groupId } : {} });
  },

  // Ejecutar scheduler manualmente (con timeout extendido para procesos largos)
  // v3.5.2: groupId opcional para ejecutar en un grupo específico
  executeScheduler: (schedulerId, groupId = null) => {
    // Timeout de 5 minutos (300 segundos) para schedulers de larga duración
    // como el de proyecciones que puede tardar hasta 3 minutos
    return api.post(`/admin/schedulers/${schedulerId}/execute`, groupId ? { groupId } : {}, {
      timeout: 300000 // 5 minutos
    });
  },

  // Obtener ejecuciones activas (v3.5.1). all=true → de todos los grupos
  getActiveExecutions: (all = false) => {
    return api.get('/admin/schedulers/executions/active', { params: all ? { all: true } : {} });
  },

  // Obtener logs de schedulers
  getLogs: (lines = 50, filter = '') => {
    const params = { lines };
    if (filter) params.filter = filter;
    return api.get('/admin/schedulers/logs', { params });
  },

  // Obtener configuración específica de un scheduler
  getSchedulerById: (schedulerId) => {
    return api.get(`/admin/schedulers/${schedulerId}`);
  },

  // Actualizar configuración de scheduler
  updateSchedulerConfig: (schedulerId, config) => {
    return api.put(`/admin/schedulers/${schedulerId}/config`, config);
  },

  // Habilitar/deshabilitar scheduler
  // v3.5.2: Acepta groupId para toggle por grupo específico
  toggleScheduler: (schedulerId, enabled, groupId = null) => {
    const payload = groupId ? { enabled, groupId } : { enabled };
    return api.post(`/admin/schedulers/${schedulerId}/toggle`, payload);
  },

  // Recargar configuración de scheduler desde BD (aplica cambios sin reiniciar backend)
  reloadScheduler: (schedulerId, groupId = null) => {
    return api.post(`/admin/schedulers/${schedulerId}/reload`, groupId ? { groupId } : {});
  },

  // Eliminar todos los scores de cobranza existentes
  clearAllScores: () => {
    return api.post('/admin/schedulers/daily_score_recalculation/clear-scores');
  }
};

// API de Renegociación de Deuda
export const negotiationAPI = {

  // === GESTIÓN DE PLANES ===

  // Obtener todos los planes con filtros
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  getPlans: (params = {}, publisherId = null, billerId = null) => {
    const { publisher_id, biller_id, ...queryFilters } = params;
    const queryParams = new URLSearchParams(queryFilters).toString();

    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }

    return api.get(`/negotiation/plans${queryParams ? '?' + queryParams : ''}`, config);
  },

  // Obtener plan por ID
  getPlanById: (planId, publisherId, billerId) => {
    return api.get(`/negotiation/plans/${planId}`, {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Crear nuevo plan
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  createPlan: (planData, publisherId = null, billerId = null) => {
    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }
    return api.post('/negotiation/plans', planData, config);
  },

  // Actualizar plan existente
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  updatePlan: (planId, planData, publisherId = null, billerId = null) => {
    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }
    return api.put(`/negotiation/plans/${planId}`, planData, config);
  },

  // Desactivar plan
  // v3.3.1: Usa headers en lugar de body para tenant context
  deactivatePlan: (planId, publisherId, billerId) => {
    const config = {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    };
    return api.delete(`/negotiation/plans/${planId}`, config);
  },

  // Validar elegibilidad de cliente para un plan
  validateCustomerEligibility: (planId, eligibilityData) => {
    return api.post(`/negotiation/plans/${planId}/validate-eligibility`, eligibilityData);
  },

  // === GESTIÓN DE CAMPAÑAS ===

  // Obtener todas las campañas con filtros
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  getCampaigns: (params = {}, publisherId = null, billerId = null) => {
    const { publisher_id, biller_id, ...queryFilters } = params;
    const queryParams = new URLSearchParams(queryFilters).toString();

    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }

    return api.get(`/negotiation/campaigns${queryParams ? '?' + queryParams : ''}`, config);
  },

  // Obtener campaña por ID
  // v3.3.1: Usa headers en lugar de query params para tenant context
  getCampaignById: (campaignId, publisherId, billerId) => {
    return api.get(`/negotiation/campaigns/${campaignId}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Crear nueva campaña
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  createCampaign: (campaignData, publisherId = null, billerId = null) => {
    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }
    return api.post('/negotiation/campaigns', campaignData, config);
  },

  // Obtener métricas de una campaña
  // v3.3.1: Usa headers en lugar de query params para tenant context
  getCampaignMetrics: (campaignId, publisherId, billerId) => {
    return api.get(`/negotiation/campaigns/${campaignId}/metrics`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener clientes de una campaña con matriz de pagos
  // v3.3.1: Usa headers para tenant context
  getCampaignCustomers: (campaignId, publisherId, billerId, filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    return api.get(`/negotiation/campaigns/${campaignId}/customers${queryParams ? '?' + queryParams : ''}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Actualizar estado de una campaña
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  updateCampaignStatus: (campaignId, statusData, publisherId = null, billerId = null) => {
    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }
    return api.put(`/negotiation/campaigns/${campaignId}/status`, statusData, config);
  },

  // Enviar notificaciones de campaña
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  sendCampaignNotifications: (campaignId, notificationData, publisherId = null, billerId = null) => {
    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }
    return api.post(`/negotiation/campaigns/${campaignId}/send-notifications`, notificationData, config);
  },

  // === SELECCIÓN DE CANDIDATOS ===

  // Seleccionar candidatos desde alertas de cobranza
  // v3.3.1: Acepta publisherId y billerId para headers explícitos
  selectCandidatesFromAlerts: (criteria, publisherId = null, billerId = null) => {
    const config = {};
    if (publisherId && billerId) {
      config.headers = {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      };
    }
    return api.post('/negotiation/candidates/from-alerts', criteria, config);
  },

  // === ENDPOINTS PÚBLICOS (sin autenticación) ===

  // Obtener datos de aceptación de plan (público - por token)
  getPublicAcceptanceData: (acceptanceToken, publisherId, billerId) => {
    return api.get(`/negotiation/public/acceptance/${acceptanceToken}`, {
      params: { publisher_id: publisherId, biller_id: billerId }
    });
  },

  // Procesar aceptación de plan (público - por token)
  processPublicAcceptance: (acceptanceToken, acceptanceData) => {
    return api.post(`/negotiation/public/acceptance/${acceptanceToken}`, acceptanceData);
  }
};

// API de Inflación (Configuración de proyecciones mensuales)
export const inflationAPI = {

  // Obtener años disponibles con datos de inflación
  getAvailableYears: (billerId, publisherId) => {
    return api.get(`/billers/${billerId}/inflation/years`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener datos de un año específico
  getYearData: (billerId, year, publisherId) => {
    return api.get(`/billers/${billerId}/inflation/${year}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Guardar/actualizar datos de un año completo
  saveYearData: (billerId, year, monthlyData, publisherId) => {
    return api.post(`/billers/${billerId}/inflation/${year}`, { monthlyData }, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Eliminar datos de un año
  deleteYear: (billerId, year, publisherId) => {
    return api.delete(`/billers/${billerId}/inflation/${year}`, {
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  },

  // Obtener datos de inflación para un período (para gráficos)
  getPeriodData: (billerId, startYear, startMonth, endYear, endMonth, publisherId) => {
    return api.get(`/billers/${billerId}/inflation/period`, {
      params: { startYear, startMonth, endYear, endMonth },
      headers: {
        'X-Publisher-ID': publisherId,
        'X-Biller-ID': billerId
      }
    });
  }
};

// API de configuración de IA
export const aiAPI = {
  // Credenciales
  getCredentials: () => api.get('/admin/ai-credentials'),
  getCredentialByProvider: (provider) => api.get(`/admin/ai-credentials/${provider}`),
  upsertCredential: (data) => api.post('/admin/ai-credentials', data),
  testConnection: (provider) => api.post(`/admin/ai-credentials/${provider}/test`),
  deleteCredential: (provider) => api.delete(`/admin/ai-credentials/${provider}`),

  // Prompts
  getPrompts: () => api.get('/admin/ai-prompts'),
  getPromptById: (id) => api.get(`/admin/ai-prompts/${id}`),
  getActivePrompt: (reportType) => api.get(`/admin/ai-prompts/active/${reportType}`),
  createPrompt: (data) => api.post('/admin/ai-prompts', data),
  updatePrompt: (id, data) => api.put(`/admin/ai-prompts/${id}`, data),
  deletePrompt: (id) => api.delete(`/admin/ai-prompts/${id}`),
  activatePrompt: (id) => api.post(`/admin/ai-prompts/${id}/activate`),
  getProviders: () => api.get('/admin/ai-prompts/providers'),

  // Usage Logs - v3.5.5
  getUsageLogs: (params) => api.get('/admin/ai-usage-logs', { params }),
  getUsageSummary: () => api.get('/admin/ai-usage-logs/summary'),
  getLogContent: (id, type) => api.get(`/admin/ai-usage-logs/${id}/content`, { params: { type } })
};

// === AI Template Generation API (v3.15.0) ===
export const aiTemplatesAPI = {
  generate: (data) => api.post('/admin/ai-templates/generate', data)
};

// === Scheduler Groups API (v3.5.0) ===
export const schedulerGroupsAPI = {
  // CRUD de grupos
  getGroups: () => api.get('/admin/scheduler-groups'),
  getGroupById: (id) => api.get(`/admin/scheduler-groups/${id}`),
  createGroup: (data) => api.post('/admin/scheduler-groups', data),
  updateGroup: (id, data) => api.put(`/admin/scheduler-groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/admin/scheduler-groups/${id}`),

  // Gestión de tenants en grupos
  getGroupTenants: (groupId) => api.get(`/admin/scheduler-groups/${groupId}/tenants`),
  assignTenants: (groupId, tenantIds) => api.post(`/admin/scheduler-groups/${groupId}/tenants`, { tenant_ids: tenantIds }),
  unassignTenant: (groupId, tenantId) => api.delete(`/admin/scheduler-groups/${groupId}/tenants/${tenantId}`),

  // Health y monitoreo
  getGroupsHealth: () => api.get('/admin/scheduler-groups/health'),
  getUnassignedTenants: () => api.get('/admin/scheduler-groups/unassigned'),
  getCurrentGroup: () => api.get('/admin/scheduler-groups/current'),

  // Auto-balanceo
  rebalanceTenants: () => api.post('/admin/scheduler-groups/rebalance')
};

// === Segment Config API ===
export const segmentConfigAPI = {
  // Obtener configuración de segmentos
  getConfig: () => api.get('/segment-config'),

  // Obtener segmentos activos con valores para filtros (usado en KPIs)
  // Requiere publisherId y billerId para el tenant context
  getActiveFilters: (publisherId, billerId) => {
    const headers = {};
    if (publisherId) headers['X-Publisher-ID'] = publisherId;
    if (billerId) headers['X-Biller-ID'] = billerId;
    return api.get('/segment-config/active-filters', { headers });
  },

  // Actualizar un segmento
  updateSegment: (segmentNumber, data) => api.put(`/segment-config/${segmentNumber}`, data),

  // Actualizar múltiples segmentos
  bulkUpdate: (segments) => api.put('/segment-config/bulk', { segments }),

  // Eliminar configuración de un segmento
  deleteSegment: (segmentNumber) => api.delete(`/segment-config/${segmentNumber}`)
};

// === Help Tooltips API === v3.6.0
export const helpTooltipsAPI = {
  // Obtener todos los tooltips (admin)
  getAll: (params = {}) => api.get('/help-tooltips', { params }),

  // Obtener categorías disponibles
  getCategories: () => api.get('/help-tooltips/categories'),

  // Obtener tooltip por ID (admin)
  getById: (id) => api.get(`/help-tooltips/${id}`),

  // Obtener tooltip por código (para componentes). lang opcional (es|en|pt)
  getByCode: (code, lang) => api.get(`/help-tooltips/by-code/${code}`, {
    params: lang ? { lang } : undefined
  }),

  // Obtener múltiples tooltips por códigos (para cargar varios a la vez). lang opcional
  getBulk: (codes, lang) => api.get('/help-tooltips/bulk', {
    params: { codes: codes.join(','), ...(lang ? { lang } : {}) }
  }),

  // Crear nuevo tooltip
  create: (data) => api.post('/help-tooltips', data),

  // Actualizar tooltip
  update: (id, data) => api.put(`/help-tooltips/${id}`, data),

  // Eliminar tooltip
  delete: (id) => api.delete(`/help-tooltips/${id}`)
};

// API de Tour Preferences (v3.7.0)
export const tourAPI = {
  // Obtener preferencia de un tour específico
  getPreference: (tourId) => api.get(`/user-preferences/tour/${tourId}`),

  // Obtener todas las preferencias de tours del usuario
  getAllPreferences: () => api.get('/user-preferences/tours'),

  // Guardar progreso del tour
  saveProgress: (tourId, data) => api.post(`/user-preferences/tour/${tourId}`, data),

  // Marcar tour como completado
  complete: (tourId) => api.post(`/user-preferences/tour/${tourId}/complete`),

  // Omitir tour
  skip: (tourId) => api.post(`/user-preferences/tour/${tourId}/skip`),

  // Reiniciar tour
  restart: (tourId) => api.post(`/user-preferences/tour/${tourId}/restart`)
};

// Payment Channels API - v3.9.0
export const paymentChannelsAPI = {
  // Channels
  getChannels: (publisherId, billerId, options = {}) =>
    api.get(`/payment-channels`, {
      params: { ...options },
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getSummary: (publisherId, billerId) =>
    api.get(`/payment-channels/summary`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getUnconfigured: (publisherId, billerId) =>
    api.get(`/payment-channels/unconfigured`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getColors: (publisherId, billerId) =>
    api.get(`/payment-channels/colors`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getChannel: (publisherId, billerId, channelId) =>
    api.get(`/payment-channels/${channelId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  createChannel: (publisherId, billerId, data) =>
    api.post(`/payment-channels`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  updateChannel: (publisherId, billerId, channelId, data) =>
    api.put(`/payment-channels/${channelId}`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  deleteChannel: (publisherId, billerId, channelId) =>
    api.delete(`/payment-channels/${channelId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  markAsConfigured: (publisherId, billerId, channelId) =>
    api.patch(`/payment-channels/${channelId}/configure`, {}, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  detectFromTransactions: (publisherId, billerId) =>
    api.post(`/payment-channels/detect-from-transactions`, {}, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),

  // Methods
  getMethods: (publisherId, billerId, channelId) =>
    api.get(`/payment-channels/${channelId}/methods`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  addMethod: (publisherId, billerId, channelId, data) =>
    api.post(`/payment-channels/${channelId}/methods`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  updateMethod: (publisherId, billerId, channelId, methodId, data) =>
    api.put(`/payment-channels/${channelId}/methods/${methodId}`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  deleteMethod: (publisherId, billerId, channelId, methodId) =>
    api.delete(`/payment-channels/${channelId}/methods/${methodId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),

  // Scales
  getMethodScales: (publisherId, billerId, methodId) =>
    api.get(`/payment-channels/methods/${methodId}/scales`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  addMethodScale: (publisherId, billerId, methodId, data) =>
    api.post(`/payment-channels/methods/${methodId}/scales`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  updateMethodScale: (publisherId, billerId, methodId, scaleId, data) =>
    api.put(`/payment-channels/methods/${methodId}/scales/${scaleId}`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  deleteMethodScale: (publisherId, billerId, methodId, scaleId) =>
    api.delete(`/payment-channels/methods/${methodId}/scales/${scaleId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),

  // Merchants (nros de comercio por método × adquirente con su cuenta de acreditación)
  getMethodMerchants: (publisherId, billerId, methodId, params = {}) =>
    api.get(`/payment-channels/methods/${methodId}/merchants`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId },
      params
    }),
  addMethodMerchant: (publisherId, billerId, methodId, data) =>
    api.post(`/payment-channels/methods/${methodId}/merchants`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  updateMethodMerchant: (publisherId, billerId, methodId, merchantId, data) =>
    api.put(`/payment-channels/methods/${methodId}/merchants/${merchantId}`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  deleteMethodMerchant: (publisherId, billerId, methodId, merchantId) =>
    api.delete(`/payment-channels/methods/${methodId}/merchants/${merchantId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),

  // Overrides de costos por adquirente (dentro de un método; alcance: DA)
  getMethodAcquirerCosts: (publisherId, billerId, methodId) =>
    api.get(`/payment-channels/methods/${methodId}/acquirer-costs`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  upsertMethodAcquirerCost: (publisherId, billerId, methodId, data) =>
    api.put(`/payment-channels/methods/${methodId}/acquirer-costs`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  deleteMethodAcquirerCost: (publisherId, billerId, methodId, globalChannelId) =>
    api.delete(`/payment-channels/methods/${methodId}/acquirer-costs/${globalChannelId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),

  // Calculation
  calculateCommission: (publisherId, billerId, data) =>
    api.post(`/payment-channels/calculate-commission`, data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getMonthlyChannelCost: (publisherId, billerId, channelId, year, month) =>
    api.get(`/payment-channels/${channelId}/monthly-cost`, {
      params: { year, month },
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  simulate: (publisherId, billerId, scenario) =>
    api.post(`/payment-channels/simulate`, scenario, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  // Cost Analysis (from pre-calculated summary tables)
  // dateRange can be { fromYear, fromMonth, toYear, toMonth } or null for defaults (last 12 months)
  getCostAnalysis: (publisherId, billerId, dateRange = null, groupBy = 'channel', segments = null) =>
    api.get(`/payment-channels/costs/analysis`, {
      params: {
        ...(dateRange ? {
          fromYear: dateRange.fromYear,
          fromMonth: dateRange.fromMonth,
          toYear: dateRange.toYear,
          toMonth: dateRange.toMonth
        } : {}),
        groupBy,
        ...(segments ? { segments } : {})
      },
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  calculateCostsNow: (publisherId, billerId, options = {}) =>
    api.post(`/payment-channels/costs/calculate-now`, options, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  // Estado del recálculo de costos (202 async → reusa el progressTracker
  // genérico de imports, con estado en Redis: responde cualquier server).
  calculateCostsStatus: (trackingId) => api.get(`/import/progress/${trackingId}/status`),
  clearCostSummaries: (publisherId, billerId) =>
    api.delete(`/payment-channels/costs/clear`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getCostAuditHistory: (publisherId, billerId, params = {}) =>
    api.get(`/payment-channels/costs/audit`, {
      params: {
        channelId: params.channelId,
        paymentMethod: params.paymentMethod,
        action: params.action,
        fromDate: params.fromDate,
        toDate: params.toDate,
        limit: params.limit || 50,
        offset: params.offset || 0
      },
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),
  getMethodAuditHistory: (publisherId, billerId, methodId) =>
    api.get(`/payment-channels/methods/${methodId}/audit`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    }),

  // Export / Import
  exportConfiguration: (publisherId, billerId) =>
    api.get('/payment-channels/export', {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId },
      responseType: 'blob'
    }),
  importConfiguration: (publisherId, billerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payment-channels/import', formData, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId, 'Content-Type': 'multipart/form-data' }
    });
  },
  // Dry-run: sólo el backend sabe qué hay hoy en esta empresa y, por lo tanto,
  // qué se lleva puesto el import.
  previewImportConfiguration: (publisherId, billerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payment-channels/import/preview', formData, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId, 'Content-Type': 'multipart/form-data' }
    });
  }
};

// v3.10.1 - Packages and Licenses System API (with Module Parameters)
export const packagesAPI = {
  // ========================================
  // PACKAGE MANAGEMENT (Platform Admin)
  // ========================================

  // List all packages
  getAll: (params = {}) => api.get('/packages', { params }),

  // Get available modules for package configuration
  getModules: () => api.get('/packages/modules'),

  // Get module parameter definitions (v3.10.1)
  getModuleParameterDefinitions: () => api.get('/packages/module-parameters/definitions'),

  // Get package by ID
  getById: (packageId) => api.get(`/packages/${packageId}`),

  // Create new package
  create: (data) => api.post('/packages', data),

  // Update package
  update: (packageId, data) => api.put(`/packages/${packageId}`, data),

  // Delete package
  delete: (packageId) => api.delete(`/packages/${packageId}`),

  // Borrado masivo con validación de uso. Responde 200 con detalle por paquete:
  // los que están en uso vienen con `deleted: false` y el motivo.
  bulkDelete: (ids) => api.post('/packages/bulk-delete', { ids }),

  // Set package as default
  setDefault: (packageId) => api.post(`/packages/${packageId}/set-default`),

  // Clone a package
  clone: (packageId) => api.post(`/packages/${packageId}/clone`),

  // Export all packages as JSON
  exportAll: () => api.get('/packages/export', { responseType: 'blob' }),

  // Export a single package as JSON
  exportOne: (id) => api.get(`/packages/${id}/export`, { responseType: 'blob' }),

  // Import packages from JSON
  importPackages: (data) => api.post('/packages/import', data),

  // Get billers with this package
  getPackageBillers: (packageId, status = null) => {
    const params = status ? { status } : {};
    return api.get(`/packages/${packageId}/billers`, { params });
  },

  // ========================================
  // BILLER PACKAGE ASSIGNMENTS (Platform Admin)
  // ========================================

  // Get packages assigned to a biller
  getBillerPackages: (billerId, includeInactive = false) => {
    const params = includeInactive ? { include_inactive: 'true' } : {};
    return api.get(`/billers/${billerId}/packages`, { params });
  },

  // Assign a package to a biller
  assignPackage: (billerId, data) => api.post(`/billers/${billerId}/packages`, data),

  // Parámetros de módulo de una empresa: valor del paquete, override y efectivo
  getBillerModuleParameters: (billerId) => api.get(`/billers/${billerId}/module-parameters`),
  setBillerModuleParameter: (billerId, moduleCode, key, { value, notes } = {}) =>
    api.put(`/billers/${billerId}/module-parameters/${moduleCode}/${key}`, { value, notes }),
  deleteBillerModuleParameter: (billerId, moduleCode, key) =>
    api.delete(`/billers/${billerId}/module-parameters/${moduleCode}/${key}`),

  // Update a package assignment
  updateAssignment: (billerId, assignmentId, data) =>
    api.put(`/billers/${billerId}/packages/${assignmentId}`, data),

  // Suspend a package assignment
  suspendAssignment: (billerId, assignmentId, reason) =>
    api.post(`/billers/${billerId}/packages/${assignmentId}/suspend`, { reason }),

  // Reactivate a suspended assignment
  reactivateAssignment: (billerId, assignmentId) =>
    api.post(`/billers/${billerId}/packages/${assignmentId}/reactivate`),

  // Cancel a package assignment
  cancelAssignment: (billerId, assignmentId, reason) =>
    api.delete(`/billers/${billerId}/packages/${assignmentId}`, { data: { reason } }),

  // Get assignment history
  getAssignmentHistory: (billerId, assignmentId) =>
    api.get(`/billers/${billerId}/packages/${assignmentId}/history`),

  // ========================================
  // BILLER ACCOUNT (For Biller Admins)
  // ========================================

  // Get current biller account summary
  getBillerAccount: () => api.get('/biller/account'),

  // Get accessible modules for current biller
  getBillerModules: () => api.get('/biller/modules'),

  // Check if biller can access a specific module
  checkModuleAccess: (moduleCode, subModule = null) => {
    const params = subModule ? { sub_module: subModule } : {};
    return api.get(`/biller/modules/${moduleCode}/check`, { params });
  },

  // Get biller usage summary (v3.10.1)
  getUsageSummary: (moduleCode = 'REPORTS', headers = {}) => {
    const params = { module: moduleCode };
    return api.get('/biller/usage/summary', { params, headers });
  },

  // ========================================
  // BILLER ACCOUNT (v3.13.0)
  // ========================================

  // Biller admin: account transactions
  getBillerAccountTransactions: (params = {}) => {
    return api.get('/biller/account/transactions', { params });
  },

  // Biller admin: account balance
  getBillerAccountBalance: () => {
    return api.get('/biller/account/balance');
  },

  // Biller admin: available packages catalog
  getAvailablePackagesForBiller: () => {
    return api.get('/biller/packages/available');
  },

  // Biller admin: submit package request
  submitPackageRequest: (data) => {
    return api.post('/biller/packages/request', data);
  },

  // Biller admin: my package requests
  getBillerPackageRequests: () => {
    return api.get('/biller/packages/requests');
  },

  // Publisher admin: biller account transactions
  getBillerAccountByAdmin: (billerId, params = {}) => {
    return api.get(`/billers/${billerId}/account`, { params });
  },

  // Publisher admin: record payment
  recordBillerPayment: (billerId, data) => {
    return api.post(`/billers/${billerId}/account/payment`, data);
  },

  // Biller discounts (v3.20.0)
  getBillerDiscounts: (billerId) => {
    return api.get(`/billers/${billerId}/discounts`);
  },
  updateBillerDiscounts: (billerId, data) => {
    return api.put(`/billers/${billerId}/discounts`, data);
  },
  getClosingDetails: (billerId, transactionId) => {
    return api.get(`/billers/${billerId}/closing-details/${transactionId}`);
  },

  // Publisher admin: all pending package requests
  getPackageRequests: (publisherId = null) => {
    const params = publisherId ? { publisher_id: publisherId } : {};
    return api.get('/billers/package-requests', { params });
  },

  // Publisher admin: pending request counts per biller
  getPackageRequestCounts: (publisherId) => {
    return api.get('/billers/package-requests/counts', { params: { publisher_id: publisherId } });
  },

  // Publisher admin: approve request
  approvePackageRequest: (requestId) => {
    return api.post(`/billers/package-requests/${requestId}/approve`);
  },

  // Publisher admin: reject request
  rejectPackageRequest: (requestId, reason) => {
    return api.post(`/billers/package-requests/${requestId}/reject`, { reason });
  },

  // Publisher admin: reset biller account (cancel all + zero balance)
  resetBillerAccount: (billerId) => {
    return api.post(`/billers/${billerId}/packages/reset-all`);
  },

  // Platform admin: pending requests for publishers with approval_mode='platform'
  getPlatformPendingRequests: (filters = {}) => {
    return api.get('/billers/package-requests/platform/pending', { params: filters });
  }
};

// v3.10.1 - Biller Usage API (convenience wrapper)
export const billerUsageAPI = {
  getSummary: (headers = {}) => api.get('/biller/usage/summary', { headers })
};

// v3.15.0 - Segment KPI Analysis API
export const segmentKpiAPI = {
  getSummary: (year) => api.get('/segment-kpi/summary', { params: { year } }),
  getEvolution: (kpi, months, segmentNumber) =>
    api.get('/segment-kpi/evolution', { params: { kpi, months, segment_number: segmentNumber } }),
  getFilteredSummary: (year, segments) =>
    api.get('/segment-kpi/filtered-summary', { params: { year, segments } })
};

// v3.17.0 - Translations Management API
export const translationsAPI = {
  getOverrides: (lang) => api.get(`/translations/${lang}`),
  getByNamespace: (lang, ns) => api.get(`/translations/${lang}/${ns}`),
  getNamespaces: () => api.get('/translations/namespaces'),
  upsert: (lang, ns, key, value) => api.put(`/translations/${lang}/${ns}/${key}`, { value }),
  remove: (lang, ns, key) => api.delete(`/translations/${lang}/${ns}/${key}`),
  importBulk: (lang, data) => api.post(`/translations/import/${lang}`, data),
};

// v3.16.0 - Countries Management API
export const countriesAPI = {
  getAll: (options = {}) => api.get('/countries', { params: options }),
  getById: (id) => api.get(`/countries/${id}`),
  getActive: () => api.get('/countries/active'),
  create: (data) => api.post('/countries', data),
  update: (id, data) => api.put(`/countries/${id}`, data),
  delete: (id) => api.delete(`/countries/${id}`),
  getAuditLog: (params = {}) => api.get('/countries/audit-log', { params }),

  // Export / Import / Wipe
  exportOne: (id) => api.get(`/countries/${id}/export`, { responseType: 'blob' }),
  exportAll: () => api.get('/countries/export-all', { responseType: 'blob' }),
  importBulk: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/countries/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAll: () => api.delete('/countries/all', { data: { confirm: 'BORRAR' } }),

  // Datos de empresa por país (doc/50)
  companyFields: {
    list: (countryId) => api.get(`/countries/${countryId}/company-fields`),
    listActive: (countryId) => api.get(`/countries/${countryId}/company-fields/active`),
    create: (countryId, data) => api.post(`/countries/${countryId}/company-fields`, data),
    update: (countryId, fieldId, data) => api.put(`/countries/${countryId}/company-fields/${fieldId}`, data),
    remove: (countryId, fieldId) => api.delete(`/countries/${countryId}/company-fields/${fieldId}`),
  },

  // Holidays sub-resource (Conciliación P4)
  holidays: {
    list: (countryId, params = {}) => api.get(`/countries/${countryId}/holidays`, { params }),
    create: (countryId, data) => api.post(`/countries/${countryId}/holidays`, data),
    update: (countryId, holidayId, data) => api.put(`/countries/${countryId}/holidays/${holidayId}`, data),
    remove: (countryId, holidayId) => api.delete(`/countries/${countryId}/holidays/${holidayId}`),
    bulkImport: (countryId, holidays) => api.post(`/countries/${countryId}/holidays/import`, { holidays })
  },

  // Taxes sub-resource (Conciliación P15)
  taxes: {
    list: (countryId, params = {}) => api.get(`/countries/${countryId}/taxes`, { params }),
    create: (countryId, data) => api.post(`/countries/${countryId}/taxes`, data),
    update: (countryId, taxId, data) => api.put(`/countries/${countryId}/taxes/${taxId}`, data),
    remove: (countryId, taxId) => api.delete(`/countries/${countryId}/taxes/${taxId}`),
    // Excel del catálogo. El .xlsx lo arma el backend con exceljs (patrón de
    // los exports de Conciliar); el mismo archivo sirve de plantilla para
    // reimportar.
    exportXlsx: (countryId) =>
      api.get(`/countries/${countryId}/taxes/export`, { responseType: 'blob' }),
    importXlsx: (countryId, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`/countries/${countryId}/taxes/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    // Recalculo de la incidencia promedio observada en las liquidaciones.
    // Recorre todos los tenants: va con tracking_id (202 + progreso por Redis)
    // para no depender del timeout del balanceador.
    averages: (countryId, body = {}) =>
      api.post(`/countries/${countryId}/taxes/averages`, body),
    averagesStatus: (trackingId) => api.get(`/import/progress/${trackingId}/status`)
  },
  // Tax aliases por adquirente (bandeja de mapeo entre source_code del archivo
  // y código canónico del país).
  taxAliases: {
    list: (countryId, params = {}) => api.get(`/countries/${countryId}/tax-aliases`, { params }),
    map: (countryId, aliasId, countryTaxId) =>
      api.post(`/countries/${countryId}/tax-aliases/${aliasId}/map`, { country_tax_id: countryTaxId }),
    createAndMap: (countryId, aliasId, data) =>
      api.post(`/countries/${countryId}/tax-aliases/${aliasId}/create-and-map`, data),
    update: (countryId, aliasId, data) =>
      api.put(`/countries/${countryId}/tax-aliases/${aliasId}`, data),
    remove: (countryId, aliasId) =>
      api.delete(`/countries/${countryId}/tax-aliases/${aliasId}`)
  }
};

// Conciliación: Plataforma → Bancos
export const banksAPI = {
  list: (params = {}) => api.get('/banks', { params }),
  getById: (id) => api.get(`/banks/${id}`),
  create: (data) => api.post('/banks', data),
  update: (id, data) => api.put(`/banks/${id}`, data),
  remove: (id) => api.delete(`/banks/${id}`),
  testRegex: ({ regex, sample }) => api.post('/banks/test-regex', { regex, sample }),

  // Configuración completa del banco (fila + reglas de extracto).
  // El export baja como archivo; el import es MERGE (no borra nada) y acepta
  // dry_run para previsualizar antes de aplicar.
  exportConfiguration: (id) => api.get(`/banks/${id}/export`, { responseType: 'blob' }),
  importConfiguration: (payload, { dryRun = false } = {}) =>
    api.post('/banks/import', { ...payload, dry_run: dryRun })
};

// Conciliación: Plataforma → Canales Globales (+ lookups)
export const globalChannelsAPI = {
  list: (params = {}) => api.get('/global-channels', { params }),
  getById: (id) => api.get(`/global-channels/${id}`),
  create: (data) => api.post('/global-channels', data),
  update: (id, data) => api.put(`/global-channels/${id}`, data),
  remove: (id) => api.delete(`/global-channels/${id}`),

  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.post(`/global-channels/${id}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (id) => api.delete(`/global-channels/${id}/logo`),

  // Export / Import / Wipe
  exportOne: (id) => api.get(`/global-channels/${id}/export`, { responseType: 'blob' }),
  exportAll: () => api.get('/global-channels/export-all', { responseType: 'blob' }),
  importBulk: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/global-channels/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAll: () => api.delete('/global-channels/all', { data: { confirm: 'BORRAR' } }),

  lookups: {
    listKinds: (channelId) => api.get(`/global-channels/${channelId}/lookup-kinds`),
    list: (channelId, params = {}) => api.get(`/global-channels/${channelId}/lookups`, { params }),
    create: (channelId, data) => api.post(`/global-channels/${channelId}/lookups`, data),
    update: (channelId, lookupId, data) => api.put(`/global-channels/${channelId}/lookups/${lookupId}`, data),
    remove: (channelId, lookupId) => api.delete(`/global-channels/${channelId}/lookups/${lookupId}`),
    bulkImport: (channelId, items) => api.post(`/global-channels/${channelId}/lookups/import`, { items })
  },

  matchStrategies: {
    registry: () => api.get('/global-channels/match-strategies/registry'),
    list: (channelId) => api.get(`/global-channels/${channelId}/match-strategies`),
    upsert: (channelId, strategies) =>
      api.put(`/global-channels/${channelId}/match-strategies`, { strategies }),
    resetDefaults: (channelId) =>
      api.post(`/global-channels/${channelId}/match-strategies/reset-defaults`),
    dryRun: (tenantContext, settlementId, strategiesOverride) =>
      api.post('/conciliacion/dry-run', { settlement_id: settlementId, strategies_override: strategiesOverride })
  }
};

// Catálogo central de marcas de tarjeta (Plataforma)
export const cardBrandsAPI = {
  list: (params = {}) => api.get('/card-brands', { params }),
  getById: (id) => api.get(`/card-brands/${id}`),
  create: (data) => api.post('/card-brands', data),
  update: (id, data) => api.put(`/card-brands/${id}`, data),
  remove: (id) => api.delete(`/card-brands/${id}`),
  exportAll: () => api.get('/card-brands/export-all'),
  importBulk: (payload) => api.post('/card-brands/import', payload)
};

// Conciliación: catálogo global de patrones de movimientos de extracto
export const bankMovementPatternsAPI = {
  list: (params = {}) => api.get('/bank-movement-patterns', { params }),
  getById: (id) => api.get(`/bank-movement-patterns/${id}`),
  create: (data) => api.post('/bank-movement-patterns', data),
  update: (id, data) => api.put(`/bank-movement-patterns/${id}`, data),
  remove: (id) => api.delete(`/bank-movement-patterns/${id}`),
  // Acepta la regla completa: { pattern | match_concepto, match_descripcion,
  // match_mode, reference_extraction_regex } + muestra { sample | concepto, descripcion }.
  test: (body) => api.post('/bank-movement-patterns/test', body)
};

// Conciliación: Configuración → Cuentas Bancarias del biller (tenant)
export const bankAccountsAPI = {
  list: (params = {}) => api.get('/bank-accounts', { params }),
  getById: (id) => api.get(`/bank-accounts/${id}`),
  create: (data) => api.post('/bank-accounts', data),
  bulkCreate: (accounts) => api.post('/bank-accounts/bulk-create', { accounts }),
  update: (id, data) => api.put(`/bank-accounts/${id}`, data),
  remove: (id) => api.delete(`/bank-accounts/${id}`),
  validate: ({ bank_id, account_number }) => api.post('/bank-accounts/validate', { bank_id, account_number }),
  // Export configuración como JSON descargable (responseType blob)
  exportConfiguration: () => api.get('/bank-accounts/export', { responseType: 'blob' }),
  // Import JSON (file = File)
  importConfiguration: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/bank-accounts/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Historial de cambios (audit log)
  getAuditHistory: (params = {}) => api.get('/bank-accounts/audit-history', { params }),
  // Cuentas virtuales (PSPCP)
  listPspcps: (params = {}) => api.get('/bank-accounts/pspcps', { params }),
  createVirtual: (data) => api.post('/bank-accounts/virtual', data),
  updateVirtual: (id, data) => api.put(`/bank-accounts/virtual/${id}`, data)
};

// Grilla de precios (iniciador×medio×plazo → comisión) — template adquirente + canal
export const pricingGridAPI = {
  getInitiators: () => api.get('/pricing-grid/initiators'),
  listTemplate: (globalChannelId) => api.get(`/pricing-grid/template/${globalChannelId}`),
  upsertTemplate: (globalChannelId, data) => api.post(`/pricing-grid/template/${globalChannelId}`, data),
  deleteTemplate: (id) => api.delete(`/pricing-grid/template/${id}`),
  exportTemplate: (globalChannelId) => api.get(`/pricing-grid/template/${globalChannelId}/export`),
  exportAllTemplates: () => api.get('/pricing-grid/templates/export'),
  importTemplates: (payload) => api.post('/pricing-grid/templates/import', payload),
  listChannel: (channelId) => api.get(`/pricing-grid/channel/${channelId}`),
  upsertChannel: (channelId, data) => api.post(`/pricing-grid/channel/${channelId}`, data),
  loadTemplate: (channelId, globalChannelId) =>
    api.post(`/pricing-grid/channel/${channelId}/load-template`, { global_channel_id: globalChannelId }),
  deleteChannel: (channelId, id) => api.delete(`/pricing-grid/channel/${channelId}/${id}`),
  validateSettlement: (settlementId) => api.get(`/pricing-grid/validate-settlement/${settlementId}`)
};

// Conciliación: extractos vía API bancaria (Interbanking) — doc/31
// Robot de descarga de archivos de adquirentes (PayWay) — doc/38.
export const robotFilesAPI = {
  listConnections: () => api.get('/robot-files/connections'),
  getConnection: (id) => api.get(`/robot-files/connections/${id}`),
  createConnection: (data) => api.post('/robot-files/connections', data),
  updateConnection: (id, data) => api.put(`/robot-files/connections/${id}`, data),
  deleteConnection: (id) => api.delete(`/robot-files/connections/${id}`),
  setState: (id, data) => api.put(`/robot-files/connections/${id}/state`, data),
  testConnection: (id) => api.post(`/robot-files/connections/${id}/test`),
  resetConnection: (id) => api.post(`/robot-files/connections/${id}/reset`),
  // Run manual async: responde { run_id } — pollear getRun hasta status != RUNNING.
  run: (id) => api.post(`/robot-files/connections/${id}/run`),
  listRuns: (params = {}) => api.get('/robot-files/runs', { params }),
  getRun: (runId) => api.get(`/robot-files/runs/${runId}`),
  listFiles: (params = {}) => api.get('/robot-files/files', { params }),
  importFile: (id, pair = false) => api.post(`/robot-files/files/${id}/import`, { pair }),
  downloadFile: (id) => api.get(`/robot-files/files/${id}/download`, { responseType: 'blob' }),
  discardFile: (id) => api.post(`/robot-files/files/${id}/discard`)
};

export const bankApiAPI = {
  listConnections: () => api.get('/bank-api/connections'),
  getConnection: (id) => api.get(`/bank-api/connections/${id}`),
  createConnection: (data) => api.post('/bank-api/connections', data),
  updateConnection: (id, data) => api.put(`/bank-api/connections/${id}`, data),
  deleteConnection: (id) => api.delete(`/bank-api/connections/${id}`),
  testConnection: (id) => api.post(`/bank-api/connections/${id}/test`),
  remoteAccounts: (id) => api.get(`/bank-api/connections/${id}/remote-accounts`),
  createLink: (connectionId, data) => api.post(`/bank-api/connections/${connectionId}/links`, data),
  provisionAccounts: (connectionId, data) => api.post(`/bank-api/connections/${connectionId}/provision`, data),
  updateLink: (linkId, data) => api.put(`/bank-api/links/${linkId}`, data),
  deleteLink: (linkId) => api.delete(`/bank-api/links/${linkId}`),
  // Sync manual async: responde { run_id } — pollear getRun hasta status != RUNNING
  syncConnection: (id) => api.post(`/bank-api/connections/${id}/sync`),
  syncLink: (linkId, data = {}) => api.post(`/bank-api/links/${linkId}/sync`, data),
  listRuns: (params = {}) => api.get('/bank-api/runs', { params }),
  getRun: (runId) => api.get(`/bank-api/runs/${runId}`),
  getAuditHistory: (params = {}) => api.get('/bank-api/audit-history', { params })
};

// Cobros/liquidaciones vía API de PSP (Mercado Pago) — doc/33
export const pspApiAPI = {
  listConnections: () => api.get('/psp-api/connections'),
  getConnection: (id) => api.get(`/psp-api/connections/${id}`),
  createConnection: (data) => api.post('/psp-api/connections', data),
  updateConnection: (id, data) => api.put(`/psp-api/connections/${id}`, data),
  deleteConnection: (id) => api.delete(`/psp-api/connections/${id}`),
  testConnection: (id) => api.post(`/psp-api/connections/${id}/test`),
  // Sync manual async: responde 202 { started }; el front pollea listRuns/getRun
  syncCobros: (id) => api.post(`/psp-api/connections/${id}/sync-cobros`),
  syncLiquidaciones: (id) => api.post(`/psp-api/connections/${id}/sync-liquidaciones`),
  listRuns: (params = {}) => api.get('/psp-api/runs', { params }),
  getRun: (runId) => api.get(`/psp-api/runs/${runId}`)
};

// Aura Conecta (API de Aura Gestiona) — cobranzas por API. Solo lectura. doc/37
export const auraConectaAPI = {
  listConnections: () => api.get('/auraconecta/connections'),
  getConnection: (id) => api.get(`/auraconecta/connections/${id}`),
  createConnection: (data) => api.post('/auraconecta/connections', data),
  updateConnection: (id, data) => api.put(`/auraconecta/connections/${id}`, data),
  deleteConnection: (id) => api.delete(`/auraconecta/connections/${id}`),
  testConnection: (id) => api.post(`/auraconecta/connections/${id}/test`),
  // Sync manual async: responde 202 { started }; el front pollea listRuns
  sync: (id) => api.post(`/auraconecta/connections/${id}/sync`),
  // Convenios pollables por conexión
  listConvenios: (id) => api.get(`/auraconecta/connections/${id}/convenios`),
  createConvenio: (id, data) => api.post(`/auraconecta/connections/${id}/convenios`, data),
  updateConvenio: (convenioId, data) => api.put(`/auraconecta/convenios/${convenioId}`, data),
  deleteConvenio: (convenioId) => api.delete(`/auraconecta/convenios/${convenioId}`),
  listRuns: (params = {}) => api.get('/auraconecta/runs', { params })
};

// Tab "Sched": administración unificada de APIs de importación — doc/34
export const integrationsAPI = {
  list: () => api.get('/integrations'),
  setState: (code, connectionId, data) => api.put(`/integrations/${code}/connections/${connectionId}/state`, data),
  run: (code, connectionId) => api.post(`/integrations/${code}/connections/${connectionId}/run`),
  listRuns: (code, connectionId, params = {}) => api.get(`/integrations/${code}/connections/${connectionId}/runs`, { params }),
  // Reset de cursor para re-importar (solo Super Admin). date opcional 'YYYY-MM-DD'.
  resetCursor: (code, connectionId, date = null) => api.post(`/integrations/${code}/connections/${connectionId}/reset-cursor`, { date })
};

// File Converters (extendido con linked_entity + valid_*)
export const fileConvertersAPI = {
  list: (params = {}) => api.get('/file-converters', { params }),
  getById: (id) => api.get(`/file-converters/${id}`),
  create: (data) => api.post('/file-converters', data),
  update: (id, data) => api.put(`/file-converters/${id}`, data),
  remove: (id) => api.delete(`/file-converters/${id}`),
  detect: (filename) => api.post('/file-converters/detect', { filename }),
  run: (code, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/file-converters/${code}/run`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Conciliación: Liquidaciones (settlements)
// Conciliación DD (sub-tab "DD"): lotes de débito directo, rechazos y cierre.
export const ddConciliacionAPI = {
  listBatches: () => api.get('/dd-conciliacion/batches'),
  getRejects: (batch) => api.get(`/dd-conciliacion/batches/${encodeURIComponent(batch)}/rejects`),
  exportRejects: (batch) => api.get(`/dd-conciliacion/batches/${encodeURIComponent(batch)}/rejects/export`, { responseType: 'blob' }),
  cierre: (batch, fechaCobro) => api.post(`/dd-conciliacion/batches/${encodeURIComponent(batch)}/cierre`, { fecha_cobro: fechaCobro }),
  revertCierre: (batch) => api.post(`/dd-conciliacion/batches/${encodeURIComponent(batch)}/cierre/revert`)
};

// DD directo Santander "Pago Directo" (Fase B1: adhesiones altas/bajas/cambios).
export const santanderDdAPI = {
  listAdhesiones: (params = {}) => api.get('/santander-dd/adhesiones', { params }),
  importAdhesiones: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/santander-dd/adhesiones/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listAcreditaciones: (params = {}) => api.get('/santander-dd/acreditaciones', { params }),
  conciliarAcreditacion: (id, bankMovementId = null) => api.post(`/santander-dd/acreditaciones/${id}/conciliar`, { bank_movement_id: bankMovementId }),
  desconciliarAcreditacion: (id) => api.post(`/santander-dd/acreditaciones/${id}/desconciliar`)
};

// Validación de CBU por lote FACDD (Débito Directo) — reporte no bloqueante.
// Intentos de presentación de una factura: reintentos por otro adquirente
// (doc/51 §10). Read-only; los registran los imports de LOTES y PAGOS.
const tenantHeaders = (publisherId, billerId) => {
  const headers = {};
  if (publisherId) headers['X-Publisher-ID'] = publisherId;
  if (billerId) headers['X-Biller-ID'] = billerId;
  return headers;
};
export const presentationAttemptsAPI = {
  byInvoice: (reminderId, publisherId, billerId) =>
    api.get(`/presentation-attempts/invoice/${reminderId}`, { headers: tenantHeaders(publisherId, billerId) }),
  batches: (publisherId, billerId) =>
    api.get('/presentation-attempts/batches', { headers: tenantHeaders(publisherId, billerId) }),
  batchDetail: (batch, role, page, publisherId, billerId) =>
    api.get('/presentation-attempts/batch', {
      params: { batch, role, page, page_size: 50 },
      headers: tenantHeaders(publisherId, billerId)
    }),
  // kind 'summary' → PDF del resumen ejecutivo; 'detail' → Excel del detalle.
  exportBatch: (batch, role, kind, publisherId, billerId) =>
    api.get('/presentation-attempts/batch/export', {
      params: { batch, role, kind },
      headers: tenantHeaders(publisherId, billerId),
      responseType: 'blob',
      timeout: 120000
    })
};

export const ddCbuValidationsAPI = {
  listByBatch: (batch) => api.get('/dd-cbu-validations', { params: { batch } }),
  exportCsv: (batch, only) => api.get('/dd-cbu-validations/export', { params: { batch, only }, responseType: 'blob' })
};

// Asistente de IA de conciliación (doc/49). Todo exige el módulo CONCILIACION_IA
// en un paquete activo; /status dice si está activo para la empresa de la sesión.
export const conciliacionIaAPI = {
  status: () => api.get('/conciliacion-ia/status'),
  getMatchCandidates: (settlementId, detailId) =>
    api.get(`/conciliacion-ia/settlements/${settlementId}/details/${detailId}/candidates`),
  getErrorCatalog: () => api.get('/conciliacion-ia/error-catalog'),
  getSettings: () => api.get('/conciliacion-ia/settings'),
  listConversations: (limit) => api.get('/conciliacion-ia/assistant/conversations', { params: limit ? { limit } : {} }),
  getUsage: () => api.get('/conciliacion-ia/assistant/usage'),
  getConversation: (id) => api.get(`/conciliacion-ia/assistant/conversations/${id}`),

  /**
   * Pregunta al asistente con streaming (Server-Sent Events). axios no expone el cuerpo
   * a medida que llega, así que va con fetch, con la misma cookie de sesión, X-Tab-ID y
   * los mismos headers de tenant que agrega axios (getTenantHeaders): con varios servers
   * (DEMO2) el caché de sesión de un proceso no lo ven los otros.
   * onEvent recibe cada evento ({ type: 'text' | 'tool' | 'quota' | 'done' | 'error' | ... }).
   */
  askStream: async ({ text, conversationId = null, context = null }, { onEvent, signal } = {}) => {
    const res = await fetch(`${API_BASE_URL}/conciliacion-ia/assistant/ask`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Tab-ID': getTabId(), ...getTenantHeaders('/conciliacion-ia/assistant/ask') },
      body: JSON.stringify({ text, conversation_id: conversationId, context }),
      signal,
    });
    if (!res.ok || !(res.headers.get('content-type') || '').includes('text/event-stream')) {
      let body = {};
      try { body = await res.json(); } catch (_) { /* sin cuerpo JSON */ }
      const err = new Error(body.error || `Error ${res.status}`);
      err.status = res.status; err.code = body.error_code; err.usage = body.usage;
      throw err;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const chunk = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) continue;
        try { onEvent && onEvent(JSON.parse(dataLine.slice(6))); } catch (_) { /* evento ilegible */ }
      }
    }
  },
};

export const settlementsAPI = {
  list: (params = {}) => api.get('/settlements', { params }),
  summary: (params = {}) => api.get('/settlements/summary', { params }),
  filterOptions: (params = {}) => api.get('/settlements/filter-options', { params }),
  getById: (id) => api.get(`/settlements/${id}`),
  // `sourceConverterId`: cuando el archivo se convirtió en el navegador, el
  // nombre que llega al backend es el del CSV de salida y ya no permite inferir
  // el conversor de origen. Declararlo es la única forma de no perder la
  // procedencia (el backend lo valida contra la empresa y el tipo).
  importCabecera: (file, sourceConverterId = null) => {
    const fd = new FormData();
    fd.append('file', file);
    if (sourceConverterId) fd.append('source_converter_id', sourceConverterId);
    return api.post('/settlements/import-cabecera', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Preview / dry-run: clasifica el archivo sin persistir nada.
  previewImportCabecera: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/settlements/import-cabecera/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Importa el detalle. Camino por defecto (patrón Facturas/Pagos — doc 23):
  // subida ÚNICA a disco + 202 async + progreso por Redis (multi-server safe).
  // Sólo si el proxy rechaza el body con 413 (p.ej. Cloudflare en DEV) cae a
  // subida por CHUNKS resumable (init → N partes de 50MB por offset → finish).
  // Devuelve la misma shape en todos los casos. onUploadProgress(pct) opcional.
  importDetalle: async (file, opts = {}) => {
    const { trackingId = null, onUploadProgress = null, sourceConverterId = null } = opts;

    // Polling del progreso async. El estado vive en progressTracker (Redis),
    // así que responde cualquier server detrás del balanceador.
    const pollUntilDone = (tid) => new Promise((resolve, reject) => {
      const t0 = Date.now();
      const MAX = 60 * 60 * 1000; // 1h tope
      const tick = async () => {
        try {
          const s = await api.get(`/import/progress/${tid}/status`);
          const d = s.data || {};
          if (d.status === 'error' || d.phase === 'error') {
            return reject(new Error(d.metadata?.error || 'Error procesando el detalle'));
          }
          if (d.phase === 'import_complete' || d.status === 'completed') {
            return resolve(d.metadata || {});
          }
        } catch (_) {
          // 404 transitorio mientras se crea la sesión → reintentar
        }
        if (Date.now() - t0 > MAX) return reject(new Error('Timeout esperando el procesamiento'));
        setTimeout(tick, 2000);
      };
      tick();
    });

    // Fallback: subida por chunks (sólo cuando el proxy corta el body grande).
    const uploadChunked = async () => {
      const CHUNK_SIZE = 50 * 1024 * 1024; // < 100MB para pasar Cloudflare
      const init = await api.post('/settlements/import-detalle/chunk/init', {
        filename: file.name, total_size: file.size
      });
      const uploadId = init.data?.uploadId;
      if (!uploadId) throw new Error('No se pudo iniciar la subida por chunks');
      let offset = 0;
      while (offset < file.size) {
        const end = Math.min(offset + CHUNK_SIZE, file.size);
        const fd = new FormData();
        fd.append('uploadId', uploadId);
        fd.append('offset', String(offset));
        fd.append('chunk', file.slice(offset, end), file.name);
        await api.post('/settlements/import-detalle/chunk', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        offset = end;
        if (onUploadProgress) { try { onUploadProgress(Math.round((offset / file.size) * 100)); } catch (_) {} }
      }
      const fin = await api.post('/settlements/import-detalle/chunk/finish', {
        uploadId, filename: file.name, total_size: file.size, tracking_id: trackingId,
        ...(sourceConverterId ? { source_converter_id: sourceConverterId } : {})
      });
      if (fin.data?.async && trackingId) {
        const stats = await pollUntilDone(trackingId);
        return { data: { success: true, ...stats } };
      }
      return fin;
    };

    // Archivos grandes → DIRECTO a chunks. Una subida única que supera el límite
    // del proxy (Cloudflare ~100MB) suele cortarse SIN devolver un 413 limpio
    // (timeout / connection reset), por lo que el fallback por 413 nunca dispara
    // y la subida se cuelga en ~1%. El umbral (< límite del proxy) fuerza chunks
    // de entrada para esos archivos (caso: detalle PayWay MX de 200MB+).
    const DIRECT_CHUNK_THRESHOLD = 80 * 1024 * 1024;
    if (file && file.size > DIRECT_CHUNK_THRESHOLD) {
      return uploadChunked();
    }

    // Camino por defecto: subida única. axios reporta el avance real de subida;
    // al llegar a 100% el modal pasa a "procesando" y pollUntilDone toma la posta.
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (trackingId) fd.append('tracking_id', trackingId);
      if (sourceConverterId) fd.append('source_converter_id', sourceConverterId);
      const resp = await api.post('/settlements/import-detalle', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress
          ? (e) => { if (e.total) { try { onUploadProgress(Math.round((e.loaded / e.total) * 100)); } catch (_) {} } }
          : undefined
      });
      if (resp.data?.async && trackingId) {
        const stats = await pollUntilDone(trackingId);
        return { data: { success: true, ...stats } };
      }
      return resp;
    } catch (e) {
      // Reintentar por chunks si el body fue demasiado grande para el proxy:
      // 413 explícito, o corte del proxy sin respuesta HTTP (timeout/reset).
      const proxyCut = e?.response?.status === 413 || !e?.response;
      if (proxyCut && file && file.size > 0) {
        return uploadChunked();
      }
      throw e;
    }
  },
  // Detalles paginados de una liquidación. Soporta search/sort/filtros
  // para que el modal pueda navegar todos los movimientos.
  getDetailsPaged: (id, params = {}) => api.get(`/settlements/${id}/details`, { params }),
  // Archivo origen (PDF) de la cabecera → blob para abrir/visualizar.
  getSourceFile: (id) => api.get(`/settlements/${id}/source-file`, { responseType: 'blob' }),
  // Cobertura de fechas: pares (fecha compra, fecha pago) de los movimientos
  // de la liquidación, con cantidad y total por par. Liquidación completa
  // (no aplica los filtros de la grilla de movimientos).
  getDateCoverage: (id) => api.get(`/settlements/${id}/date-coverage`),
  conciliateOne: (id) => api.post(`/settlements/${id}/conciliate`),
  // Re-conciliar: borra matches automáticos del settlement (preservando los
  // manuales) y vuelve a correr el conciliador. Útil tras un fix del engine
  // o cambio de configuración en settlements con matches previos.
  reconcileOne: (id) => api.post(`/settlements/${id}/reconcile`),
  conciliateAll: (limit = 50, trackingId = null) =>
    api.post('/settlements/conciliate-all', { limit, tracking_id: trackingId }),
  // Estado de progreso del "Conciliar pendientes" (progressTracker genérico).
  // Lo comparte "Validar comisiones", que reporta por el mismo mecanismo.
  conciliateStatus: (trackingId) => api.get(`/import/progress/${trackingId}/status`),
  // Valida la comisión liquidada de TODAS las liquidaciones que matchean los
  // filtros (no sólo la página). 202 + progreso por trackingId.
  validateCommissions: (filters = {}, trackingId = null) =>
    api.post('/settlements/validate-commissions', { filters, tracking_id: trackingId }),
  // Play/Pause del scheduler por liquidación. Sin body = toggle.
  toggleAutoConciliacion: (id, enabled) =>
    api.post(`/settlements/${id}/toggle-auto-conciliacion`,
      typeof enabled === 'boolean' ? { enabled } : {}),
  deleteOne: (id) => api.delete(`/settlements/${id}`),
  dryRun: (id, strategiesOverride = null) =>
    api.post(`/settlements/${id}/dry-run`, strategiesOverride ? { strategies_override: strategiesOverride } : {}),
  matchStrategyStats: (globalChannelId, days = 30) =>
    api.get(`/settlements/match-strategies/stats/${globalChannelId}`, { params: { days } }),
  matchStrategyStatsRefresh: (dateFrom = null) =>
    api.post('/settlements/match-strategies/stats/refresh', dateFrom ? { date_from: dateFrom } : {}),

  // Conciliación manual de un detalle individual.
  // kind ∈ 'PAYMENT' | 'PREVIOUS_DETAIL' | 'OK_MANUAL'.
  manualMatchDetail: (settlementId, detailId, { kind, target_id, notes } = {}) =>
    api.post(`/settlements/${settlementId}/details/${detailId}/manual-match`,
      { kind, target_id, notes }),
  deleteManualMatchDetail: (settlementId, detailId) =>
    api.delete(`/settlements/${settlementId}/details/${detailId}/manual-match`),

  // Historial de intentos (auto + manual) de una liquidación.
  getConciliationAttempts: (settlementId) =>
    api.get(`/settlements/${settlementId}/attempts`),

  // OK manual masivo: marca todos los detalles sin match como manual_ok=1.
  bulkManualOkRemaining: (settlementId, notes = null) =>
    api.post(`/settlements/${settlementId}/details/bulk-manual-ok`, { notes })
};

// Conciliación 2: Extractos bancarios (bank_statement_movements)
// Conciliar → Cobros: cobros de cliente por transferencia y su imputación a facturas (doc/52 F3)
export const bankCollectionsAPI = {
  list: (params = {}) => api.get('/bank-collections', { params }),
  getById: (id) => api.get(`/bank-collections/${id}`),
  searchInvoices: (search) => api.get('/bank-collections/invoices', { params: { search } }),
  auto: (id) => api.post(`/bank-collections/${id}/auto`),
  impute: (id, body) => api.post(`/bank-collections/${id}/impute`, body),
  revert: (imputationId) => api.post(`/bank-collections/imputations/${imputationId}/revert`)
};

export const bankStatementsAPI = {
  list: (params = {}) => api.get('/bank-statements', { params }),
  getById: (id) => api.get(`/bank-statements/${id}`),
  statsSummary: (params = {}) => api.get('/bank-statements/stats/summary', { params }),
  detect: (filename) => api.post('/bank-statements/detect', { filename }),
  preview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/bank-statements/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  import: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/bank-statements/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  conciliateBankAll: (limit = 100) => api.post('/bank-statements/conciliate-all', { limit }),
  conciliateBankOneSettlement: (settlementId) => api.post(`/bank-statements/settlements/${settlementId}/conciliate`),
  // Borrado masivo (requiere body { confirm: "Eliminar datos" })
  deleteAll: (confirm, reason) => api.delete('/bank-statements/all', { data: { confirm, reason } }),
  deleteByAccount: (bankAccountId, confirm, reason) =>
    api.delete(`/bank-statements/by-account/${bankAccountId}`, { data: { confirm, reason } }),
  // Audit log (imports + delete_all + delete_account + manual_net)
  getAuditHistory: (params = {}) => api.get('/bank-statements/audit-history', { params }),
  // Neteo manual de liquidaciones negativas contra un crédito (credit-only)
  nettingCandidates: (movementId) => api.get(`/bank-statements/${movementId}/netting-candidates`),
  // extra: { mode: 'OFFSET_CHARGE', charge_settlement_id, charge_tax_id } para
  // la compensación de negativas contra un cargo de la liquidación positiva.
  netSettlements: (movementId, settlementIds, reason, extra = {}) =>
    api.post(`/bank-statements/${movementId}/net-settlements`, { settlement_ids: settlementIds, reason, ...extra }),
  revertNetSettlements: (movementId, reason) =>
    api.delete(`/bank-statements/${movementId}/net-settlements`, { data: { reason } })
};

// Conciliación Fase 5: Impuestos (sub-tab Impuestos del módulo Conciliar)
export const settlementTaxesAPI = {
  summary: (params = {}) => api.get('/settlement-taxes/summary', { params }),
  uncatalogued: (params = {}) => api.get('/settlement-taxes/uncatalogued', { params }),
  filterOptions: (params = {}) => api.get('/settlement-taxes/filter-options', { params }),
  channelStats: (params = {}) => api.get('/settlement-taxes/channel-stats', { params }),
  detail: (taxCode, params = {}) => api.get(`/settlement-taxes/${encodeURIComponent(taxCode)}/detail`, { params }),
  exportXlsx: (params = {}) => api.get('/settlement-taxes/export.xlsx', { params, responseType: 'blob' }),
  exportDetailXlsx: (taxCode, params = {}) =>
    api.get(`/settlement-taxes/${encodeURIComponent(taxCode)}/detail.xlsx`, { params, responseType: 'blob' }),
  exportDetailPdf: (taxCode, params = {}) =>
    api.get(`/settlement-taxes/${encodeURIComponent(taxCode)}/detail.pdf`, { params, responseType: 'blob' }),
  // v3.25.0 - Carga manual de retenciones (adquirentes sin impuestos en archivo, ej. AMEX GRRCN)
  converterConfig: (settlementId) =>
    api.get(`/settlement-taxes/converter-config/${settlementId}`),
  createManual: (settlementId, data) =>
    api.post('/settlement-taxes/manual', { settlement_id: settlementId, ...data }),
  updateManual: (taxId, data) =>
    api.put(`/settlement-taxes/manual/${taxId}`, data),
  deleteManual: (taxId) =>
    api.delete(`/settlement-taxes/manual/${taxId}`)
};

// Conciliación Fase 5: Cargos (mismo backend que taxes, distinta categoría
// y permission gate). Apunta a /settlement-charges para que el permiso se
// verifique contra conciliacion/cargos.
export const settlementChargesAPI = {
  summary: (params = {}) => api.get('/settlement-charges/summary', { params }),
  uncatalogued: (params = {}) => api.get('/settlement-charges/uncatalogued', { params }),
  filterOptions: (params = {}) => api.get('/settlement-charges/filter-options', { params }),
  channelStats: (params = {}) => api.get('/settlement-charges/channel-stats', { params }),
  detail: (taxCode, params = {}) => api.get(`/settlement-charges/${encodeURIComponent(taxCode)}/detail`, { params }),
  exportXlsx: (params = {}) => api.get('/settlement-charges/export.xlsx', { params, responseType: 'blob' }),
  exportDetailXlsx: (taxCode, params = {}) =>
    api.get(`/settlement-charges/${encodeURIComponent(taxCode)}/detail.xlsx`, { params, responseType: 'blob' }),
  exportDetailPdf: (taxCode, params = {}) =>
    api.get(`/settlement-charges/${encodeURIComponent(taxCode)}/detail.pdf`, { params, responseType: 'blob' })
};

// Conciliación Fase 5: Contracargos (mismo backend que taxes, restringido
// a CONTRACARGO + MULTA, permission gate conciliacion/contracargos).
export const settlementContracargosAPI = {
  summary: (params = {}) => api.get('/settlement-contracargos/summary', { params }),
  uncatalogued: (params = {}) => api.get('/settlement-contracargos/uncatalogued', { params }),
  filterOptions: (params = {}) => api.get('/settlement-contracargos/filter-options', { params }),
  reasonStats: (params = {}) => api.get('/settlement-contracargos/reason-stats', { params }),
  detail: (taxCode, params = {}) => api.get(`/settlement-contracargos/${encodeURIComponent(taxCode)}/detail`, { params }),
  exportXlsx: (params = {}) => api.get('/settlement-contracargos/export.xlsx', { params, responseType: 'blob' }),
  exportDetailXlsx: (taxCode, params = {}) =>
    api.get(`/settlement-contracargos/${encodeURIComponent(taxCode)}/detail.xlsx`, { params, responseType: 'blob' }),
  exportDetailPdf: (taxCode, params = {}) =>
    api.get(`/settlement-contracargos/${encodeURIComponent(taxCode)}/detail.pdf`, { params, responseType: 'blob' }),
  // Vista "Por tarjeta" — datos de ACMW_Central.credit_cards / credit_card_chargebacks
  // filtrados al biller actual.
  cardsList: (params = {}) => api.get('/settlement-contracargos/by-card', { params }),
  cardHistory: (cardHash, params = {}) =>
    api.get(`/settlement-contracargos/by-card/${encodeURIComponent(cardHash)}/history`, { params }),
  // Imputación manual del contracargo/devolución al cobro original (movimiento
  // negativo). detailId = settlement_details.id.
  impute: (detailId) => api.post(`/settlement-contracargos/detail/${detailId}/impute`),
  revertImputation: (detailId) => api.post(`/settlement-contracargos/detail/${detailId}/revert-imputation`)
};

// Conciliación Fase 4: Calendario de acreditaciones
export const calendarAPI = {
  range: (params = {}) => api.get('/calendar/range', { params }),
  summary: (params = {}) => api.get('/calendar/summary', { params }),
  dayDetail: (date, params = {}) => api.get(`/calendar/day/${date}`, { params }),
  dayReport: (date, params = {}) => api.get(`/calendar/day/${date}/report`, {
    params, responseType: 'blob'
  }),
  // Canales para el filtro (los que el calendario tiene datos de). No usa
  // /payment-channels: ese exige configuracion.canales y un Biller User que ve
  // el Calendario no lo tiene.
  channels: () => api.get('/calendar/channels'),
  recalculate: (body = {}) => api.post('/calendar/recalculate', body),
  // Estado de progreso del recálculo (reusa el progressTracker genérico de imports)
  recalcStatus: (trackingId) => api.get(`/import/progress/${trackingId}/status`),
  // Preferencia por usuario: modo del modal de día (simple | advanced)
  getDayModalMode: () => api.get('/user-preferences/kv', { params: { key: 'calendar_day_modal_mode' } }),
  setDayModalMode: (value) => api.post('/user-preferences/kv', { key: 'calendar_day_modal_mode', value })
};

// v3.24.0 - Flujo (Analizar > Flujo): proyectado vs liquidado vs conciliado
export const flujoAPI = {
  range: (params = {}) => api.get('/flujo/range', { params }),
  channels: () => api.get('/flujo/channels'),
  acquirers: (params = {}) => api.get('/flujo/acquirers', { params })
};

// Conciliación Fase 6 - Asientos: plan de cuentas, centros de costo, mapeo, generación
export const accountingAPI = {
  meta: () => api.get('/accounting/meta'),
  // Plan de cuentas
  listAccounts: (params = {}) => api.get('/accounting/accounts', { params }),
  getAccount: (id) => api.get(`/accounting/accounts/${id}`),
  createAccount: (data) => api.post('/accounting/accounts', data),
  updateAccount: (id, data) => api.put(`/accounting/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/accounting/accounts/${id}`),
  // Centros de costo
  listCostCenters: (params = {}) => api.get('/accounting/cost-centers', { params }),
  createCostCenter: (data) => api.post('/accounting/cost-centers', data),
  updateCostCenter: (id, data) => api.put(`/accounting/cost-centers/${id}`, data),
  deleteCostCenter: (id) => api.delete(`/accounting/cost-centers/${id}`),
  // Mapeo de imputación
  listMappings: (params = {}) => api.get('/accounting/mappings', { params }),
  createMapping: (data) => api.post('/accounting/mappings', data),
  updateMapping: (id, data) => api.put(`/accounting/mappings/${id}`, data),
  deleteMapping: (id) => api.delete(`/accounting/mappings/${id}`),
  // Audit
  auditHistory: (params = {}) => api.get('/accounting/audit-history', { params }),
  // Export / Import config
  exportConfig: () => api.get('/accounting/config/export', { responseType: 'blob' }),
  importConfig: (formData) => api.post('/accounting/config/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Formatos de exportación (catálogo central, disponible para todas las empresas)
  listFormats: (params = {}) => api.get('/accounting/export-formats', { params }),
  setFormatSettings: (code, params) => api.put(`/accounting/export-formats/${code}/settings`, { params }),
  // Generación de asientos
  pendingSettlements: (params = {}) => api.get('/accounting/pending-settlements', { params }),
  preview: (body = {}) => api.post('/accounting/preview', body),
  generate: (body = {}) => api.post('/accounting/generate', body),
  listJobs: (params = {}) => api.get('/accounting/jobs', { params }),
  getJob: (id) => api.get(`/accounting/jobs/${id}`),
  jobEntries: (id) => api.get(`/accounting/jobs/${id}/entries`),
  downloadJob: (id) => api.get(`/accounting/jobs/${id}/download`, { responseType: 'blob' }),
  revertJob: (id) => api.post(`/accounting/jobs/${id}/revert`),
  // Preferencia por usuario: último destino de exportación elegido
  getExportDestino: () => api.get('/user-preferences/kv', { params: { key: 'accounting_export_destino' } }),
  setExportDestino: (value) => api.post('/user-preferences/kv', { key: 'accounting_export_destino', value })
};

// v3.18.0 - Notification Activation API
export const notificationActivationAPI = {
  getBatchSummary: (params = {}, publisherId, billerId) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/notification-activation/batch-summary?${queryString}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  getAvailablePlans: (params = {}, publisherId, billerId) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/notification-activation/available-plans?${queryString}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  activate: (data, publisherId, billerId) => {
    return api.post('/notification-activation/activate', data, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  stopCampaign: (configId, publisherId, billerId) => {
    return api.put(`/notification-activation/${configId}/stop`, {}, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  resumeCampaign: (configId, publisherId, billerId) => {
    return api.put(`/notification-activation/${configId}/resume`, {}, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  removeCampaign: (configId, publisherId, billerId) => {
    return api.delete(`/notification-activation/${configId}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  getCampaignKPI: (configId, publisherId, billerId) => {
    return api.get(`/notification-activation/${configId}/kpi`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  getBatchSegmentValues: (params, publisherId, billerId) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/notification-activation/batch-segment-values?${queryString}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  },
  getFilterPreview: (params, publisherId, billerId) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/notification-activation/batch-filter-preview?${queryString}`, {
      headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
    });
  }
};

// API de Custom Placeholders
export const customPlaceholdersAPI = {
  // Platform (admin) endpoints
  getPlatform: () => api.get('/custom-placeholders/admin'),
  createPlatform: (data) => api.post('/custom-placeholders/admin', data),
  updatePlatform: (id, data) => api.put(`/custom-placeholders/admin/${id}`, data),
  deletePlatform: (id) => api.delete(`/custom-placeholders/admin/${id}`),

  // Biller (tenant) endpoints
  getBiller: (publisherId, billerId) => api.get('/custom-placeholders/', {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),
  createBiller: (publisherId, billerId, data) => api.post('/custom-placeholders/', data, {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),
  updateBiller: (publisherId, billerId, id, data) => api.put(`/custom-placeholders/${id}`, data, {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),
  deleteBiller: (publisherId, billerId, id) => api.delete(`/custom-placeholders/${id}`, {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),

  // Merge platform + biller (for template editor)
  getAll: (publisherId, billerId) => api.get('/custom-placeholders/all', {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  })
};

export const externalApisAPI = {
  // Platform (Admin) - API Registry
  listApis: (params = {}) => api.get('/external-apis/admin', { params }),
  getApi: (id) => api.get(`/external-apis/admin/${id}`),
  createApi: (data) => api.post('/external-apis/admin', data),
  updateApi: (id, data) => api.put(`/external-apis/admin/${id}`, data),
  deleteApi: (id) => api.delete(`/external-apis/admin/${id}`),
  testApi: (id, testData) => api.post(`/external-apis/admin/${id}/test`, testData),
  saveTestCredentials: (id, credentials) => api.put(`/external-apis/admin/${id}/test-credentials`, { credentials }),
  getTestCredentials: (id) => api.get(`/external-apis/admin/${id}/test-credentials`),

  // Platform Stats
  getGlobalStats: (params = {}) => api.get('/external-apis/admin/stats', { params }),
  getApiStats: (id, params = {}) => api.get(`/external-apis/admin/${id}/stats`, { params }),
  getInvocationLog: (id, params = {}) => api.get(`/external-apis/admin/${id}/invocations`, { params }),

  // Biller - Credentials
  listCredentials: (publisherId, billerId) => api.get('/external-apis/credentials', {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),
  saveCredentials: (publisherId, billerId, apiId, data) => api.put(`/external-apis/credentials/${apiId}`, data, {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),
  testCredentials: (publisherId, billerId, apiId, testData = {}) => api.post(`/external-apis/credentials/${apiId}/test`, testData, {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId }
  }),
  getBillerApiStats: (publisherId, billerId, apiId, params = {}) => api.get(`/external-apis/credentials/${apiId}/stats`, {
    headers: { 'X-Publisher-ID': publisherId, 'X-Biller-ID': billerId },
    params
  }),

  // Shared - Available APIs (for placeholder form)
  getAvailableApis: (publisherId, billerId) => api.get('/external-apis/available', {
    params: { publisherId, billerId }
  }),
};

// v3.19.0 - Operations (Force Logout)
export const operationsAPI = {
  getStatus: () => api.get('/admin/operations/force-logout/status'),
  initiate: (data) => api.post('/admin/operations/force-logout/initiate', data),
  cancel: (eventId, reason) => api.post(`/admin/operations/force-logout/${eventId}/cancel`, { reason }),
  getHistory: (params = {}) => api.get('/admin/operations/force-logout/history', { params }),
  check: () => api.get('/admin/operations/force-logout/check'),
  // Maintenance Windows
  listWindows: () => api.get('/admin/operations/maintenance-windows'),
  createWindow: (data) => api.post('/admin/operations/maintenance-windows', data),
  updateWindow: (id, data) => api.put(`/admin/operations/maintenance-windows/${id}`, data),
  toggleWindow: (id, isActive) => api.post(`/admin/operations/maintenance-windows/${id}/toggle`, { isActive }),
  deleteWindow: (id) => api.delete(`/admin/operations/maintenance-windows/${id}`),
  checkCronConflict: (cronSchedule) => api.post('/admin/operations/maintenance-windows/check-cron', { cronSchedule })
};

// Partner Programs
export const partnersAPI = {
  listPrograms: () => api.get('/admin/partners/programs'),
  createProgram: (data) => api.post('/admin/partners/programs', data),
  updateProgram: (id, data) => api.put(`/admin/partners/programs/${id}`, data),
  toggleProgram: (id, isActive) => api.post(`/admin/partners/programs/${id}/toggle`, { isActive }),
  deleteProgram: (id) => api.delete(`/admin/partners/programs/${id}`),
  getAudit: (params = {}) => api.get('/admin/partners/audit', { params }),
  exportPrograms: () => api.get('/admin/partners/programs/export'),
  importPrograms: (data) => api.post('/admin/partners/programs/import', data)
};

export const rolesAPI = {
  exportRoles: (id) => api.get('/roles/export', { params: id ? { id } : {} }),
  importRoles: (data) => api.post('/roles/import', data)
};

// Cost Optimizer API
export const costOptimizerAPI = {
  listVectors: (billerId) =>
    api.get(`/billers/${billerId}/cost-optimizer/opportunity-cost-vectors`),
  getVector: (billerId, id) =>
    api.get(`/billers/${billerId}/cost-optimizer/opportunity-cost-vectors/${id}`),
  createVector: (billerId, data) =>
    api.post(`/billers/${billerId}/cost-optimizer/opportunity-cost-vectors`, data),
  updateVector: (billerId, id, data) =>
    api.put(`/billers/${billerId}/cost-optimizer/opportunity-cost-vectors/${id}`, data),
  deleteVector: (billerId, id) =>
    api.delete(`/billers/${billerId}/cost-optimizer/opportunity-cost-vectors/${id}`),
  simulate: (billerId, options) =>
    api.post(`/billers/${billerId}/cost-optimizer/simulate`, options),
  getAvailableScopes: (billerId, periodMonths = 3) =>
    api.get(`/billers/${billerId}/cost-optimizer/available-scopes`, {
      params: { period_months: periodMonths }
    })
};

// Exportar la instancia de api para uso directo
export { api };

